import React, { useEffect, useMemo, useState } from "react";
import { TeletorgetHero } from "../hero";
import axios from "axios";
import { BuyMinutesSection } from "../../buyminutessection.tsx";
import { money } from "../../../utilities/currency";
import Swal from "sweetalert2";
import Agents from "../../agents.tsx";
import EventModules from "../eventmodules.jsx";
import { Blogs } from "../blogs.jsx";
import { FestivalAds } from "../festivalads.jsx";
import { BadgeCheck, ChevronDown, CreditCard, ExternalLink, Info, Lock, MessageSquare, Phone, PhoneCall, Receipt, Shield, Video, Wallet } from "lucide-react";
import Badge from "../badge.jsx";
import { Button } from "../button.jsx";
import { cn } from "../../../utilities/conversions.js";
import { Card, CardContent, CardHeader, CardTitle } from "../card.jsx";
import { motion } from "framer-motion";

const stepBadges = [
    { id: 1, label: "Build website" },
    { id: 2, label: "Connect channels" },
    { id: 3, label: "Go live" },
];
type CmsAndInnholdModuleProps = {
    onRegisterClick: () => void,
    baseUrl: string,
    client: any,
    currentUser: any
    onLoginClick: () => void,
    onLoginSuccess: () => void,
    setActiveTab: (tab: string) => void,
}
type Mode = "test" | "live";
export default function CmsAndInnholdModule({ onRegisterClick, baseUrl, client, currentUser, onLoginClick, onLoginSuccess, setActiveTab }: CmsAndInnholdModuleProps) {
    const default_configs = client ? JSON.parse(client.default_configs) : {};
    const invoice_inputs = client ? JSON.parse(client.invoice_inputs) : {};
    // THEME STATE
    const theme_settings = client.theme_settings ? JSON.parse(client.theme_settings) : {};
    const wallet_settings = theme_settings.wallet_settings ? theme_settings.wallet_settings : {
        image: "../../wallet-form-side.png",
        order: "right"
    };

    const event_settings = theme_settings.event_settings ? theme_settings.event_settings : [
        {
            id: "live",
            image: "https://images.unsplash.com/photo-1551836022-4c4c79ecde51?auto=format&fit=crop&w=1200&q=80",
        },
        {
            id: "upcoming",
            image: "https://images.unsplash.com/photo-1503428593586-e225b39bddfe?auto=format&fit=crop&w=1200&q=80",
        },
        {
            id: "agents",
            image: "https://images.unsplash.com/photo-1595152772835-219674b2a8a6?auto=format&fit=crop&w=1200&q=80",
        },

    ]

    const [primaryColor, setPrimaryColor] = useState(theme_settings?.primaryColor || "#0C5FD1");
    const [secondaryColor, setSecondaryColor] = useState(theme_settings?.secondaryColor || "#f6f6f7");
    const [useGradient, setUseGradient] = useState(theme_settings.useGradient ? theme_settings.useGradient : false);

    const [appearance, setAppearance] = useState({
        gradientEnabled: useGradient,
        gradientFrom: primaryColor,
        gradientTo: secondaryColor,
    });

    const [selectedBlocks, setSelectedBlocks] = useState<{ [key: string]: string | null }>({
        hero: null,
        agents: theme_settings.agent_settings?.selectedAgent ? theme_settings.agent_settings?.selectedAgent : null,
        blog: null,
        video: null,
        cta: "ctaA",
        wallet: theme_settings.wallet_settings?.selectedWallet ? theme_settings.wallet_settings.selectedWallet : "walletA",
        events: theme_settings.event_settings?.selectedEvent ? theme_settings.event_settings?.selectedEvent : "eventsA",
        skills: theme_settings.skill_settings?.selectedSkill ? theme_settings.skill_settings?.selectedSkill : null,
    });


    const blockOptions = {
        hero: [
            {
                id: "heroA",
                name: "Hero A – Left text, right form",
                description: "Conversion-focused hero with title, subtext and lead capture form.",
            },
            {
                id: "heroB",
                name: "Hero B – Centered with video",
                description: "Centered hero with big headline and video placeholder.",
            },
            {
                id: "heroC",
                name: "Hero C – Split gradient",
                description: "Gradient background with text on the left and feature bullets on the right.",
            },
        ],
        ...(default_configs?.wallet_payment === "yes" && {
            wallet: [
                {
                    id: "walletA",
                    name: "Wallet A – Basic wallet",
                    description: "Simple wallet interface with balance and transaction history.",
                },
            ],
        }),
        agents: [
            {
                id: "agentsA",
                name: "Agent cards – 4 in a row",
                description: "Profile cards with image, name, tags and CTA.",
            },
            {
                id: "agentsB",
                name: "Agent list – Compact",
                description: "Vertical list with status badge and contact actions.",
            },
        ],
        events: [
            {
                id: "eventsA",
                name: "Event cards - 3 in a row",
                description: "Event cards with image and filter",
            },
        ],
        skills: [
            {
                id: "skillsA",
                name: "Skill cards - 6 in a row",
                description: "Skill cards with icon onclick filtering agents",
            },
        ],
        blog: [
            {
                id: "blogA",
                name: "Blog grid",
                description: "3-column grid of latest posts pulled from CMS/API.",
            },
            {
                id: "blogB",
                name: "Blog list",
                description: "Vertical list with category and read time.",
            },
        ],
        video: [
            {
                id: "videoA",
                name: "Livestream section",
                description: "Upcoming live events with Join buttons.",
            },
            {
                id: "videoB",
                name: "Video library",
                description: "On-demand video cards with filters.",
            },
        ],
        cta: [
            {
                id: "ctaA",
                name: "Simple CTA",
                description: "Single call-to-action with button and short text.",
            },
            {
                id: "ctaB",
                name: "Multi-column CTA",
                description: "3-column value props with CTA.",
            },
        ],
    };

    const T = {
        offer: "✨ Spesialtilbud – Betal 99 kr, få 198 kr i ringe kreditt! ✨",
        signIn: "Logg inn",
        join: "Bli med",
        allAdvisors: "Alle veiledere",
        festivalTitle: "Festivalkampanjer",
        referTitle: "Få din første konsultasjon gratis!",
        referBody: "Book en 15-minutters gratis samtale. Perfekt for å finne riktig juridisk rådgiver – trygt, digitalt og uten forpliktelser.",
        blogTitle: "Fra Mystic‑bloggen",
        viewAll: "Se alle",
        subscribeLine: "Få nye guider og tilbud på e‑post.",
        read: "Les",
        invite: "Book gratis konsultasjon",
        sponsored: "Sponset",
        ad: (n: number) => `Annonse ${n}`,
        adBlurb: "Annonser produkter, apper eller arrangementer her.",
        learnMore: "Les mer",
        connectNow: "Koble til nå",
        from: "fra",
        minutes: "min",
        verified: "Verifisert",
        categories: [
            "Kjærlighetsreading",
            "Klarsynt reading",
            "Tarot‑reading",
            "Astrologi & Horoskop",
            "Englebudskap",
            "Orakel‑veiledning",
            "Drømmetydning",
            "Åndelig medium",
        ],
        hero: {
            name: "Advokatene",
            tag: "Kjærlighetsekspert",
            blurb:
                "Mitt løfte: Jeg er her for deg. Når det er vanskelig å vite hvem man kan stole på, er ekte nærvær alt.",
            getReading: "Bestill konsultasjon",
        },
        footer: {
            getApp: "Få appen for klarhet hvor du enn er.",
            explore: "Utforsk",
            special: "Spesialiteter",
            more: "Mer",
            advisors: "Veiledere",
            blog: "Bloggen vår",
            applyCode: "Bruk kampanjekode",
            support: "Kundesupport",
            about: "Om oss",
            apply: "Veileder? Søk her",
            privacy: "Personvern",
            terms: "Vilkår",
        },
        wallet: {
            title: "Kjøp ringeminutter",
            intro:
                "Minutter lagres i din Mystic‑wallet og kan brukes hos alle veiledere (chat / samtale / video).",
            numLabel: "Telefonnummeret du ringer fra",
            amount: "Velg beløp",
            buy: (amt: number) => `Kjøp ${money(amt)} kreditt`,
            learn: "Les mer om betalingsmåter",
            err: "Skriv inn et gyldig telefonnummer",
            otp: (n: string) => `Kode sendt til ${n}. Skriv den inn for å bekrefte.`,
            enterOtp: "Skriv inn kode",
            verify: "Bekreft & legg til kreditt",
            cancel: "Avbryt",
            success: (amt: number) => `${money(amt)} lagt til i lommeboken!`,
            pm1: "Vipps MobilePay",
            pm2: "Kort (krever registrering på ditt mobilnummer)",
            nolgin: "Ingen innlogging nødvendig for Vipps MobilePay – ring direkte.",
        },
    };

    const [mode, setMode] = useState<"blueprint" | "template">("blueprint");

    const handleSelect = (category: keyof typeof blockOptions, id: string) => {
        setSelectedBlocks((prev) => ({
            ...prev,
            [category]: prev[category] === id ? null : id,
        }));
    };

    const isSelected = (category: keyof typeof blockOptions, id: string) => {
        return selectedBlocks[category] === id;
    };

    const handleChipClick = (skill: any) => {
        console.log("handle chip click");
    };



    // HERO STATE
    const [heroData, setHeroData] = useState({
        title: "",
        body: "",
        images: [] as any[],
        primaryColor: primaryColor,
        secondaryColor: secondaryColor,
        useGradient: useGradient,
        wallet: {
            image: "../../wallet-form-side.png",
            order: "right",
        },
        events: [
            {
                id: "live",
                image: "https://images.unsplash.com/photo-1551836022-4c4c79ecde51?auto=format&fit=crop&w=1200&q=80",
            },
            {
                id: "upcoming",
                image: "https://images.unsplash.com/photo-1503428593586-e225b39bddfe?auto=format&fit=crop&w=1200&q=80",
            },
            {
                id: "agents",
                image: "https://images.unsplash.com/photo-1595152772835-219674b2a8a6?auto=format&fit=crop&w=1200&q=80",
            },

        ],
        blogs: {},
        skills: {},
        agents: {},
    })

    useEffect(() => {
        return () => {
            heroData?.images?.forEach(img => {
                if (img !== null && img.startsWith("blob:")) URL.revokeObjectURL(img);
            });
        };
    }, [heroData?.images]);

    const handleSaveAndContinueClick = async () => {
        let formData = new FormData();
        heroData.imageFiles?.forEach((file, index) => {
            formData.append("images[]", file);
        });

        //remove images and primaryColor and secondaryColor from heroData before appending
        const updatedHeroData = {
            selectedHero: {
                [selectedBlocks.hero]: {
                    title: heroData.title,
                    body: heroData.body,
                    images: null,
                },
            },
        };

        if (heroData.images) {
            updatedHeroData.selectedHero[selectedBlocks.hero].images = heroData.images;
        }

        formData.append("heroData", JSON.stringify(updatedHeroData));
        formData.append("secondaryColor", secondaryColor);
        formData.append("primaryColor", primaryColor);
        formData.append("useGradient", useGradient.toString());
        formData.append("token", currentUser?.token);
        formData.append("client_uid", client?.uid);
        if (heroData.wallet) {
            formData.append("wallet_file", heroData.wallet.file);
            // delete heroData.wallet.file;
            heroData.wallet.selectedWallet = selectedBlocks.wallet;
            formData.append("wallet", JSON.stringify(heroData.wallet));
        }
        if (heroData.events) {
            formData.append("events", JSON.stringify(heroData.events));
        }
        if (selectedBlocks.blog) {
            heroData.blogs = { selectedBlog: selectedBlocks.blog };
            formData.append("blogs", JSON.stringify(heroData.blogs));
        }
        if (selectedBlocks.skills) {
            heroData.skills = { selectedSkill: selectedBlocks.skills };
            formData.append("skills", JSON.stringify(heroData.skills));
        }
        if (selectedBlocks.agents) {
            heroData.agents = { selectedAgent: selectedBlocks.agents };
            formData.append("agents", JSON.stringify(heroData.agents));
        }
        console.log(updatedHeroData);

        const response = await axios.post(baseUrl + '/saveCmsSettings', formData);
        if (response.data.status === "OK") {
            Swal.fire({
                icon: 'success',
                title: 'Innstillinger lagret',
                showConfirmButton: false,
                timer: 1500
            });
            setStepActive(1);
            // setTimeout(() => {
            //     window.location.reload();
            // }, 1500);
        }
    }

    const handleGoLiveClick = async () => {
        let formData = new FormData();
        const services = [
            ...items,
            {
                id: "agents",
                group: "Agents",
                title: "Agent seats",
                subtitle: "Agent occupancy price per seat",
                active: true,
                monthly: agentSeatPrice,
                quantity: agentSeats,
                article_no: 2002,
            },
        ];
        const servicesPayload = services.map(({ icon, ...rest }) => rest);

        formData.append("services", JSON.stringify(servicesPayload));
        formData.append('token', currentUser.token);
        formData.append('client_uid', client.uid);

        try {
            const response = await axios.post(baseUrl + '/createOrUpdateClientSubscription', formData);

            if (response.data.status === "OK") {
                Swal.fire({
                    title: "Success",
                    icon: 'success',
                    text: response.data.data,
                })
            } else {
                Swal.fire({
                    title: 'Failed',
                    icon: 'error',
                    text: response.data.data,
                });
            }
        } catch (e) {
            console.error(e);
        }
    }

    const [clientPriceLists, setClientPriceLists] = useState([]);

    const getClientPriceList = async () => {
        const response = await axios.get(baseUrl + '/fetchClientPriceList', { params: { token: currentUser?.token, client_uid: client?.uid } });
        if (response.data.status === "OK") {
            setClientPriceLists(response.data.data);
        }
    }

    useEffect(() => {
        if (!client || !currentUser || !baseUrl) return;

        getClientPriceList();
    }, [baseUrl, client, currentUser])

    const handleStepTwoSaveAndContinueClick = async () => {
        let formData = new FormData();
        let default_configs = {
            "vipps_pay": vippsEnabled ? "yes" : "no",
            "card_payment": stripeEnabled ? "yes" : "no",
            "wallet_payment": walletEnabled ? "yes" : "no",
            "vipps_account_mode": vippsMode,
            "vipps_client_id": vippsClientId,
            "vipps_client_secret": vippsClientSecret,
            "vipps_subscription_key": vippsPrimarySubKey,
            "vipps_msn": vippsMerchantSerial,
            "test_phone_number": testPhoneNumber,
            "sms_service": smsEnabled ? "yes" : "no",
            "send_payment_sms": smsPayment ? "yes" : "no",
            "alert_agent_when_available": smsAgentLogin ? "yes" : "no",
            "agent_nick_sms": smsAgentNick ? "yes" : "no",
            "WebRTC": state.webrtcAudio ? "yes" : "no",
            "webinar": state.webinarStreaming ? "yes" : "no",
            "video": state.videocall11 ? "yes" : "no",
            "video_one_to_one_charge_per_minute": state.perMinute ? "yes" : "no",
            "video_one_to_one_charge_per_conference": state.perConference ? "yes" : "no",
            "join_with_link": state.joinWithLink ? "yes" : "no",
            "wallet_service": walletService,
            "wallet_payout": walletPayout,
            "ivr": audioEnabled ? "yes" : "no",
        }

        formData.append("default_configs", JSON.stringify(default_configs));
        formData.append("invoice_inputs", JSON.stringify(invoiceInputs));
        // update in domain
        formData.append("stripe_account_use", stripeMode);
        formData.append("stripe_api_key", stripeSk);
        formData.append("stripe_pub_key", stripePk);
        // update in client
        formData.append("brand_name", smsSenderId);
        formData.append("twilio_account_sid", twilioSid);
        formData.append("twilio_auth_token", twilioToken);
        formData.append("token", currentUser.token);
        formData.append("client_uid", client.uid);

        const response = await axios.post(baseUrl + '/updateIcsSettings', formData);

        if (response.data.status === "OK") {
            Swal.fire({
                icon: 'success',
                title: 'Innstillinger lagret',
                showConfirmButton: false,
                timer: 1500
            });
            if(localStorage.getItem("supportNavigateFrom") === "Innstillinger") {
                localStorage.removeItem("supportNavigateFrom");
                setActiveTab("Innstillinger");
            }else{
                setStepActive(2);
            }
        }
    }

    const [viewport, setViewport] = useState<"desktop" | "tablet" | "mobile">("desktop");

    useEffect(() => {
        if (!theme_settings) return;

        setPrimaryColor(theme_settings.primaryColor ?? "#0C5FD1");
        setSecondaryColor(theme_settings.secondaryColor ?? "#f6f6f7");
        setUseGradient(theme_settings.useGradient === "true");

        setAppearance({
            gradientFrom: theme_settings.primaryColor ?? "#0C5FD1",
            gradientTo: theme_settings.secondaryColor ?? "#f6f6f7",
            gradientEnabled: theme_settings.useGradient === "true",
        });

        setSelectedBlocks(prev => ({
            ...prev,
            hero: theme_settings.selectedHeroKey && theme_settings.selectedHeroKey !== "null"
                ? theme_settings.selectedHeroKey
                : null,
            wallet: wallet_settings.selectedWallet && wallet_settings.selectedWallet !== "null"
                ? wallet_settings.selectedWallet
                : null,
            blog: theme_settings?.blog_settings?.selectedBlog && theme_settings?.blog_settings?.selectedBlog !== "null"
                ? theme_settings.blog_settings.selectedBlog
                : null,
            agents: theme_settings?.agent_settings?.selectedAgent && theme_settings?.agent_settings?.selectedAgent !== "null"
                ? theme_settings.agent_settings.selectedAgent
                : null,
            skills: theme_settings?.skill_settings?.selectedSkill && theme_settings?.skill_settings?.selectedSkill !== "null"
                ? theme_settings.skill_settings.selectedSkill
                : null,
        }));

    }, [
        theme_settings?.primaryColor,
        theme_settings?.secondaryColor,
        theme_settings?.useGradient,
        theme_settings?.selectedHeroKey,
    ]);

    useEffect(() => {
        setHeroData(prev => ({
            ...prev,
            wallet: wallet_settings,
            events: event_settings,
            useGradient: useGradient,
            primaryColor,
            secondaryColor,
        }));
    }, [primaryColor, secondaryColor, client, useGradient]);

    const advisors = useMemo(() => [
        {
            id: '1',
            uid: 'agent_isu2De',
            name: 'Name 1',
            code: 1113,
            call_status: 'busy',
            customer_minute_price: 120,
            verified: true,
            skills: "skill1 skill2 skill3",
            is_active: true,
            customer_call_startup_fee: 5,
            receiving_calls_on: 'phone',
            can_video_call: 1,
            phonebill: '',
            display_pic: 'https://images.unsplash.com/photo-1544723795-3fb6469f5b39?auto=format&fit=crop&w=700&q=80',
            permissions: {
                booking: 1
            }

        },
        {
            id: '2',
            uid: 'agent_tk34ssD',
            name: "Name 2",
            code: 1114,
            call_status: 'offline',
            customer_minute_price: 120,
            verified: false,
            skills: "skill3",
            is_active: true,
            customer_call_startup_fee: 5,
            receiving_calls_on: 'web',
            can_video_call: 1,
            phonebill: '',
            display_pic: 'https://images.unsplash.com/photo-1500917293891-ef795e70e1f6?auto=format&fit=crop&w=700&q=80',
            permissions: {
                booking: 0
            }
        },
        {
            id: '3',
            uid: 'agent_s96deD',
            name: "Name 3",
            code: 1115,
            call_status: 'online',
            customer_minute_price: 120,
            verified: true,
            skills: "skill1",
            is_active: true,
            customer_call_startup_fee: 5,
            receiving_calls_on: 'phone',
            can_video_call: 0,
            phonebill: '47XXXXXXXX',
            display_pic: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=700&q=80',
            permissions: {
                booking: 1
            }
        },
        {
            id: '4',
            uid: 'agent_p8dd9Se',
            name: "Name 5",
            code: 1116,
            call_status: 'offline',
            customer_minute_price: 120,
            verified: true,
            skills: "skill4",
            is_active: true,
            customer_call_startup_fee: 5,
            receiving_calls_on: 'phone',
            can_video_call: 1,
            phonebill: '',
            display_pic: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=500&h=750&q=80',
            permissions: {
                booking: 1
            }
        },
    ], []);

    const [filteredAdvisors, setFilteredAdvisors] = useState(advisors);

    const [searchField, setSearchField] = useState("");
    const onSearchClick = () => {
        if (!searchField.trim()) {
            setFilteredAdvisors(advisors);  // reset if empty
            return;
        }

        const keyword = searchField.toLowerCase();

        const results = advisors.filter((a) =>
            a.name?.toLowerCase().includes(keyword)
        );

        setFilteredAdvisors(results);
    }

    useEffect(() => {
        if (!searchField) {
            onSearchClick();
        }
    }, [searchField]);

    const [stepActive, setStepActive] = useState(Number(localStorage.getItem("stepActive")) || 0);

    useEffect(() => {
        const step = Number(localStorage.getItem("stepActive")) || 0;
        setStepActive(step);
    }, []);

    // START Step 2 States And Functions
    // High-level toggles
    const [vippsEnabled, setVippsEnabled] = useState(false);
    const [stripeEnabled, setStripeEnabled] = useState(false);
    const [walletEnabled, setWalletEnabled] = useState(false);
    const [invoiceEnabled, setInvoiceEnabled] = useState(false);

    // Expand/collapse details
    const [open, setOpen] = useState<null | "vipps" | "stripe" | "wallet" | "invoice">("");

    // Modes
    const [vippsMode, setVippsMode] = useState<Mode>("test");
    const [stripeMode, setStripeMode] = useState<Mode>("test");
    const [walletMode, setWalletMode] = useState<Mode>("test");

    // Keys (demo)
    const [stripeSk, setStripeSk] = useState("");
    const [stripePk, setStripePk] = useState("");

    const [vippsClientId, setVippsClientId] = useState("");
    const [vippsClientSecret, setVippsClientSecret] = useState("");
    const [vippsPrimarySubKey, setVippsPrimarySubKey] = useState("");
    const [vippsMerchantSerial, setVippsMerchantSerial] = useState("");
    const [testPhoneNumber, setTestPhoneNumber] = useState("");

    const [walletService, setWalletService] = useState<"minutes" | "consult" | "course">("minutes");
    const [walletPayout, setWalletPayout] = useState<"client" | "csv">("client");

    const summaryPayments = useMemo(() => {
        const active: string[] = [];
        if (vippsEnabled) active.push("Vipps");
        if (stripeEnabled) active.push("Stripe");
        if (walletEnabled) active.push("Wallet");
        if (invoiceEnabled) active.push("Invoice");
        return active.length ? active.join(" · ") : "No channels enabled";
    }, [vippsEnabled, stripeEnabled, walletEnabled, invoiceEnabled]);

    const SelectContext = React.createContext<any>(null);

    function Select({ value, onValueChange, children }: any) {
        const [open, setOpen] = React.useState(false);
        return (
            <SelectContext.Provider value={{ value, onValueChange, open, setOpen }}>
                <div className="relative">{children}</div>
            </SelectContext.Provider>
        );
    }

    function SelectTrigger({ className = "", children }: any) {
        const ctx = React.useContext(SelectContext);
        return (
            <button
                type="button"
                onClick={() => ctx.setOpen(!ctx.open)}
                className={
                    "flex w-full items-center justify-between rounded-xl border border-slate-300 bg-white px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 " +
                    className
                }
            >
                {children}
                <ChevronDown className="ml-2 h-4 w-4 text-slate-500" />
            </button>
        );
    }

    function SelectValue({ placeholder }: any) {
        const ctx = React.useContext(SelectContext);
        return (
            <span className={ctx.value ? "text-slate-900" : "text-slate-400"}>
                {ctx.value || placeholder}
            </span>
        );
    }

    function SelectContent({ children }: any) {
        const ctx = React.useContext(SelectContext);
        if (!ctx.open) return null;
        return (
            <div className="absolute z-50 mt-2 w-full rounded-xl border border-slate-200 bg-white shadow-lg">
                {children}
            </div>
        );
    }

    function SelectItem({ value, children }: any) {
        const ctx = React.useContext(SelectContext);
        const active = ctx.value === value;
        return (
            <div
                onClick={() => {
                    ctx.onValueChange(value);
                    ctx.setOpen(false);
                }}
                className={
                    "cursor-pointer px-3 py-2 text-sm hover:bg-slate-100 " +
                    (active ? "bg-slate-100 font-medium" : "")
                }
            >
                {children}
            </div>
        );
    }
    function MiniField({
        label,
        value,
        onChange,
        sensitive,
    }: {
        label: string;
        value: string;
        onChange: (v: string) => void;
        sensitive?: boolean;
    }) {
        const [show, setShow] = useState(false);
        const type = sensitive ? (show ? "text" : "password") : "text";
        return (
            <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                    <label className="text-[12px] font-medium text-slate-700">{label}</label>
                    {sensitive ? (
                        <button
                            type="button"
                            className="h-7 px-2 text-[12px] text-slate-600 hover:text-slate-900"
                            onClick={() => setShow((s) => !s)}
                        >
                            <Lock className="mr-1 h-3.5 w-3.5" />
                            {show ? "Hide" : "Show"}
                        </button>
                    ) : null}
                </div>
                <input
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    type={type}
                    className="flex w-full border border-input px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-10 rounded-xl bg-white"
                />
            </div>
        );
    }

    function Switch({ checked, onCheckedChange, disabled = false }: any) {
        return (
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                disabled={disabled}
                onClick={() => !disabled && onCheckedChange(!checked)}
                className={
                    "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] " +
                    (checked ? "bg-[var(--primary-color)]" : "bg-red-500") +
                    (disabled ? " opacity-50 cursor-not-allowed" : " cursor-pointer")
                }
            >
                <span
                    className={
                        "inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform " +
                        (checked ? "translate-x-5" : "translate-x-1")
                    }
                />
            </button>
        );
    }

    function ToggleLine({
        title,
        description,
        checked,
        onChange,
    }: {
        title: string;
        description?: string;
        checked: boolean;
        onChange: (v: boolean) => void;
    }) {
        return (
            <div className="flex items-start justify-between gap-4 rounded-xl border border-slate-200 bg-white/70 p-4">
                <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900">{title}</div>
                    {description ? (
                        <div className="mt-1 text-[12.5px] leading-5 text-slate-600">{description}</div>
                    ) : null}
                </div>
                <Switch checked={checked} onCheckedChange={onChange} />
            </div>
        );
    }

    function sanitizeSenderId(value: string) {
        // Common SMS sender ID rules: A–Z, 0–9, space optional, max ~11.
        // We'll enforce: uppercase A–Z, 0–9, and space; max 11.
        const upper = value.toUpperCase();
        const cleaned = upper.replace(/[^A-Z0-9 ]/g, "");
        return cleaned.slice(0, 11);
    }

    function Row({
        id,
        icon,
        title,
        tag,
        desc,
        enabled,
        setEnabled,
        warning,
    }: {
        id: "vipps" | "stripe" | "wallet" | "invoice";
        icon: React.ReactNode;
        title: string;
        tag: React.ReactNode;
        desc: string;
        enabled: boolean;
        setEnabled: (v: boolean) => void;
        warning?: React.ReactNode;
    }) {
        const isOpen = open === id;
        return (
            <>
                <div className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-xl border border-slate-200 bg-white/70 p-4">
                    <div className="flex min-w-0 items-start gap-3">
                        <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">{icon}</div>
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <div className="text-sm font-semibold text-slate-900">{title}</div>
                                {tag}
                                {!enabled ? (
                                    <Badge variant="outline" className="rounded-full text-slate-500">
                                        Disabled
                                    </Badge>
                                ) : (
                                    <Badge className="rounded-full bg-emerald-600">Active</Badge>
                                )}
                            </div>
                            <div className="mt-1 text-[12.5px] leading-5 text-slate-600">{desc}</div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="hidden items-center gap-2 sm:flex">
                            <label className="text-[12px] text-slate-600">Enable</label>
                            <Switch checked={enabled} onCheckedChange={setEnabled} />
                        </div>
                        <button onClick={() => setOpen((cur) => (cur === id ? null : id))} className="inline-flex items-center gap-1 rounded-full border border-[var(--primary-color)] bg-[var(--secondary-color)] text-dark text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">
                            Details
                            <ChevronDown className={cn("ml-2 h-4 w-4 transition", isOpen && "rotate-180")} />
                        </button>
                    </div>
                </div>

                {isOpen ? (
                    <div className="mt-3 rounded-2xl border border-slate-200 bg-white/70 p-4">
                        <div className="flex flex-col gap-3">
                            {warning ? <div>{warning}</div> : null}
                            {id !== "invoice" && (
                                <div className="space-y-2">
                                    <label className="text-[12px] font-medium text-slate-700">Mode</label>
                                    <Select
                                        value={
                                            id === "vipps" ? vippsMode : id === "stripe" ? stripeMode : walletMode
                                        }
                                        onValueChange={(v) => {
                                            const m = v as Mode;
                                            if (id === "vipps") setVippsMode(m);
                                            if (id === "stripe") setStripeMode(m);
                                            if (id === "wallet") setWalletMode(m);
                                        }}
                                    >
                                        <SelectTrigger className="h-10 rounded-xl bg-white">
                                            <SelectValue placeholder="Select one" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="test">Test</SelectItem>
                                            <SelectItem value="live">Live</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <p className="text-[12px] leading-5 text-slate-600">
                                        Start in Test mode to validate flows before switching to Live.
                                    </p>
                                </div>
                            )}
                            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">

                                {id === "vipps" ? (
                                    <div className="lg:col-span-2">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <div className="text-[12px] font-semibold text-slate-800">Vipps keys</div>
                                            <a
                                                href="#"
                                                className="inline-flex items-center gap-1 text-[12px] font-medium text-[var(--primary-color)] hover:underline"
                                            >
                                                Create Vipps business account <ExternalLink className="h-3.5 w-3.5" />
                                            </a>
                                        </div>
                                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                            <MiniField label="Client ID" value={vippsClientId} onChange={setVippsClientId} />
                                            <MiniField
                                                label="Client Secret"
                                                value={vippsClientSecret}
                                                onChange={setVippsClientSecret}
                                                sensitive
                                            />
                                            <MiniField
                                                label="Primary Subscription Key"
                                                value={vippsPrimarySubKey}
                                                onChange={setVippsPrimarySubKey}
                                            />
                                            <MiniField
                                                label="Merchant Serial Number"
                                                value={vippsMerchantSerial}
                                                onChange={setVippsMerchantSerial}
                                            />
                                            {vippsMode === "test" && (
                                                <MiniField
                                                    label="Test phone number"
                                                    value={testPhoneNumber}
                                                    onChange={setTestPhoneNumber}
                                                />
                                            )}
                                        </div>
                                        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                                            <button className="inline-flex items-center gap-1 border border-[var(--primary-color)] rounded-full bg-[var(--secondary-color)] text-dark text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">
                                                Validate
                                            </button>
                                            <button className="inline-flex items-center gap-1 rounded-full bg-[var(--primary-color)] text-white text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">Save Vipps</button>
                                        </div>
                                    </div>
                                ) : null}

                                {id === "stripe" ? (
                                    <div className="lg:col-span-2">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <div className="text-[12px] font-semibold text-slate-800">Stripe keys</div>
                                            <a
                                                href="#"
                                                className="inline-flex items-center gap-1 text-[12px] font-medium text-blue-700 hover:underline"
                                            >
                                                Create Stripe business account <ExternalLink className="h-3.5 w-3.5" />
                                            </a>
                                        </div>
                                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                            <MiniField label="Stripe API key" value={stripeSk} onChange={setStripeSk} sensitive />
                                            <MiniField label="Stripe pub key" value={stripePk} onChange={setStripePk} />
                                        </div>
                                        <div className="mt-3 rounded-xl border border-slate-200 bg-white/70 p-3">
                                            <div className="text-[12px] font-semibold text-slate-800">Split payment limitations</div>
                                            <p className="mt-1 text-[12.5px] leading-5 text-slate-600">
                                                Split payments depend on Stripe account setup and country. Link the client to Stripe’s latest
                                                terms for connected accounts.
                                            </p>
                                            <a
                                                href="#"
                                                className="mt-2 inline-flex items-center gap-1 text-[12px] text-blue-700 hover:underline"
                                            >
                                                Open Stripe terms <ExternalLink className="h-3.5 w-3.5" />
                                            </a>
                                        </div>
                                        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                                            <button className="inline-flex items-center gap-1 border border-[var(--primary-color)] rounded-full bg-[var(--secondary-color)] text-dark text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">
                                                Validate
                                            </button>
                                            <button className="inline-flex items-center gap-1 rounded-full bg-[var(--primary-color)] text-white text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">Save Stripe</button>

                                        </div>
                                    </div>
                                ) : null}

                                {id === "wallet" ? (
                                    <div className="lg:col-span-2">
                                        <div className="text-[12px] font-semibold text-slate-800">Wallet settings</div>
                                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                            <div className="space-y-2">
                                                <label className="text-[12px] font-medium text-slate-700">Service (ICS)</label>
                                                <Select value={walletService} onValueChange={(v) => setWalletService(v as any)}>
                                                    <SelectTrigger className="h-10 rounded-xl bg-white">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="minutes">Prepaid calling minutes</SelectItem>
                                                        <SelectItem value="consult">Consultation credits</SelectItem>
                                                        <SelectItem value="course">Course access credits</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-[12px] font-medium text-slate-700">Settlement</label>
                                                <Select value={walletPayout} onValueChange={(v) => setWalletPayout(v as any)}>
                                                    <SelectTrigger className="h-10 rounded-xl bg-white">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="client">Route 100% to client</SelectItem>
                                                        <SelectItem value="csv">Reconcile via CSV</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                                            <button className="inline-flex items-center gap-1 border border-[var(--primary-color)] rounded-full bg-[var(--secondary-color)] text-dark text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">
                                                Download wallet CSV
                                            </button>
                                            <button className="inline-flex items-center gap-1 rounded-full bg-[var(--primary-color)] text-white text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">Save Wallet</button>
                                        </div>
                                    </div>
                                ) : null}

                                {id === "invoice" ? (
                                    <div className="lg:col-span-2">
                                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                            <MiniField label="Bank Account" value={invoiceInputs.bank_account} onChange={(v) => setInputs("bank_account", v)} />
                                            <MiniField label="Due Date (days after the invoice sent)" value={invoiceInputs.due_date_days} onChange={(v) => setInputs("due_date_days", v)} />
                                        </div>
                                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                            <MiniField label="Email message (body)" value={invoiceInputs.email_body} onChange={(v) => setInputs("email_body", v)} />
                                            <MiniField label="Reminders (body)" value={invoice_inputs.reminder_body} onChange={(v) => setInputs("reminder_body", v)} />
                                        </div>
                                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                            <MiniField label="Reminder sent after (if not paid within due date)" value={invoiceInputs.reminder_sent_after} onChange={(v) => setInputs("reminder_sent_after", v)} />
                                            <MiniField label="Invoice number (starting no e.g 1000)" value={invoiceInputs.invoice_number} onChange={(v) => setInputs("invoice_number", v)} />
                                        </div>
                                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                            <MiniField label="Email draft (invoice will be emailed here too)" value={invoiceInputs.email_draft} onChange={(v) => setInputs("email_draft", v)} />
                                            <div className="space-b-2">
                                                <label className="text-[12px] font-medium text-slate-700">Draft generated</label>
                                                <Select value={invoiceInputs.draft_generated} onValueChange={(v) => setInputs("draft_generated", v)}>
                                                    <SelectTrigger className="h-10 rounded-xl bg-white">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="auto">Automatically</SelectItem>
                                                        <SelectItem value="manual">Manually</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                ) : null}
            </>
        );
    }

    function Pill({ children }: { children: React.ReactNode }) {
        return (
            <span className="inline-flex items-center rounded-full border border-slate-200 bg-white/70 px-2.5 py-1 text-[12px] text-slate-700 shadow-sm">
                {children}
            </span>
        );
    }

    function Notice({
        tone = "info",
        title,
        children,
    }: {
        tone?: "info" | "warn";
        title: string;
        children: React.ReactNode;
    }) {
        const styles =
            tone === "warn"
                ? "border-amber-200 bg-amber-50/70 text-amber-900"
                : "border-blue-200 bg-blue-50/70 text-blue-900";
        const icon = tone === "warn" ? <Info className="h-4 w-4" /> : <Shield className="h-4 w-4" />;
        return (
            <div className={cn("rounded-xl border p-4", styles)}>
                <div className="flex items-start gap-3">
                    <div className="mt-0.5 rounded-lg bg-white/70 p-2 shadow-sm">{icon}</div>
                    <div className="min-w-0">
                        <div className="text-sm font-semibold">{title}</div>
                        <div className="mt-1 text-[12.5px] leading-5 opacity-95">{children}</div>
                    </div>
                </div>
            </div>
        );
    }

    // Expand/collapse
    const [openSmsOrVideo, setOpenSmsOrVideo] = useState<string | null>(null);

    // SMS
    const [smsEnabled, setSmsEnabled] = useState(true);
    // const [smsMode, setSmsMode] = useState<Mode>("test");
    const [smsSenderId, setSmsSenderId] = useState("");
    const [smsPayment, setSmsPayment] = useState(true);
    const [smsChatAlert, setSmsChatAlert] = useState(false);
    const [smsAgentLogin, setSmsAgentLogin] = useState(false);
    const [smsAgentNick, setSmsAgentNick] = useState(true);

    // Provider keys (demo)
    const [smsProvider, setSmsProvider] = useState<"twilio" | "sonetel" | "none">("none");
    const [twilioSid, setTwilioSid] = useState("ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx");
    const [twilioToken, setTwilioToken] = useState("xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx");

    // Video (WebRTC)
    const [videoEnabled, setVideoEnabled] = useState(true);
    const [audioEnabled, setAudioEnabled] = useState(true);
    const [sendLinkToCustomer, setSendLinkToCustomer] = useState(true);
    const [sendLinkVia, setSendLinkVia] = useState<"sms" | "email" | "both">("both");


    const summarySmsAndVideo = useMemo(() => {
        const active: string[] = [];
        if (smsEnabled) active.push("SMS");
        if (videoEnabled) active.push("WebRTC");
        if (audioEnabled) active.push("IVR");
        return active.length ? active.join(" · ") : "No services enabled";
    }, [smsEnabled, videoEnabled, audioEnabled]);

    function ServiceRow({
        id,
        icon,
        title,
        tag,
        desc,
        enabled,
        setEnabled,
        open,
        setOpen,
        children,
    }: {
        id: string;
        icon: React.ReactNode;
        title: string;
        tag: React.ReactNode;
        desc: string;
        enabled: boolean;
        setEnabled: (v: boolean) => void;
        open: string | null;
        setOpen: (v: string | null) => void;
        children: React.ReactNode;
    }) {
        const isOpen = open === id;
        return (
            <>
                <div className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-xl border border-slate-200 bg-white/70 p-4">
                    <div className="flex min-w-0 items-start gap-3">
                        <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">{icon}</div>
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <div className="text-sm font-semibold text-slate-900">{title}</div>
                                {tag}
                                {!enabled ? (
                                    <Badge variant="outline" className="rounded-full text-slate-500">
                                        Disabled
                                    </Badge>
                                ) : (
                                    <Badge className="rounded-full bg-emerald-600">Active</Badge>
                                )}
                            </div>
                            <div className="mt-1 text-[12.5px] leading-5 text-slate-600">{desc}</div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="hidden items-center gap-2 sm:flex">
                            <label className="text-[12px] text-slate-600">Enable</label>
                            <Switch checked={enabled} onCheckedChange={setEnabled} />
                        </div>
                        <button onClick={() => setOpen(isOpen ? null : id)} className="inline-flex items-center gap-1 rounded-full border border-[var(--primary-color)] bg-[var(--secondary-color)] text-dark text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">
                            Details
                            <ChevronDown className={cn("ml-2 h-4 w-4 transition", isOpen && "rotate-180")} />
                        </button>
                    </div>
                </div>

                {isOpen ? (
                    <div className="mt-3 rounded-2xl border border-slate-200 bg-white/70 p-4">{children}</div>
                ) : null}
            </>
        );
    }

    function ModeSelect({ value, onChange }: { value: Mode; onChange: (m: Mode) => void }) {
        return (
            <div className="space-y-2">
                <label className="text-[12px] font-medium text-slate-700">Mode</label>
                <Select value={value} onValueChange={(v) => onChange(v as Mode)}>
                    <SelectTrigger className="h-10 rounded-xl bg-white">
                        <SelectValue placeholder="Select one" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="test">Test</SelectItem>
                        <SelectItem value="live">Live</SelectItem>
                    </SelectContent>
                </Select>
                <p className="text-[12px] leading-5 text-slate-600">Start in Test mode to validate flows before Live.</p>
            </div>
        );
    }

    // END Step 2 States And Functions

    // START Step 3 States And Functions

    type VCOMState = {
        webrtcAudio: boolean;
        webinarStreaming: boolean;
        videocall11: boolean;
        perMinute: boolean;
        perConference: boolean;
        joinWithLink: boolean;
    };
    type INPUTState = {
        bank_account: string;
        due_date_days: string,
        email_body: string,
        reminder_body: string,
        reminder_sent_after: string,
        invoice_number: string,
        email_draft: string,
        draft_generated: string,
    };

    function enforcePolicy(prev: VCOMState, key: keyof VCOMState, val: boolean): VCOMState {
        const next: VCOMState = { ...prev, [key]: val } as VCOMState;

        // Policy: If join-with-link is enabled, disable direct 1:1 audio + direct 1:1 videocall
        if (key === "joinWithLink" && val === true) {
            next.webrtcAudio = false;
            next.videocall11 = false;
        }

        return next;
    }
    function enforceInput(prev: INPUTState, key: keyof INPUTState, val: string): INPUTState {
        const next: INPUTState = { ...prev, [key]: val } as INPUTState;
        return next;
    }

    const [state, setState] = React.useState<VCOMState>({
        webrtcAudio: true,
        webinarStreaming: true,
        videocall11: true,
        perMinute: true,
        perConference: true,
        joinWithLink: false,
    });

    const [invoiceInputs, setInvoiceInputs] = React.useState<INPUTState>({
        bank_account: "",
        due_date_days: "",
        email_body: "",
        reminder_body: "",
        reminder_sent_after: "",
        invoice_number: "",
        email_draft: "",
        draft_generated: "",
    });

    const set = (key: keyof VCOMState, val: string) => setState((s) => enforcePolicy(s, key, val));
    const setInputs = (key: keyof INPUTState, val: string) => setInvoiceInputs((s) => enforceInput(s, key, val));
    const lockedByLinkOnly = state.joinWithLink;

    type OptionRowProps = {
        title: string;
        description?: string;
        value: boolean;
        onChange: (next: boolean) => void;
        disabled?: boolean;
        note?: string;
    };

    function OptionRow({
        title,
        description,
        value,
        onChange,
        disabled = false,
        note,
    }: OptionRowProps) {
        return (
            <div
                className={
                    "grid grid-cols-[1fr_auto] items-center gap-4 py-4 " +
                    (disabled ? "opacity-70" : "")
                }
            >
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <div className="text-sm font-semibold text-slate-800 truncate">{title}</div>
                        {note ? (
                            <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                                {note}
                            </span>
                        ) : null}
                    </div>
                    {description ? <div className="mt-0.5 text-xs text-slate-500">{description}</div> : null}
                </div>

                <div className="flex flex-col items-end gap-1">
                    <span
                        className={
                            "text-[11px] font-semibold uppercase tracking-wide " +
                            (value ? "text-green-700" : "text-red-700")
                        }
                    >
                        {value ? "Enabled" : "Disabled"}
                    </span>
                    <Switch checked={value} onCheckedChange={onChange} disabled={disabled} />
                </div>
            </div>
        );
    }

    type SectionProps = {
        title: string;
        subtitle?: string;
        children: React.ReactNode;
    };

    function Section({ title, subtitle, children }: SectionProps) {
        return (
            <div className="rounded-2xl border border-slate-200 bg-white">
                <div className="px-5 pt-5">
                    <div className="text-sm font-semibold text-slate-900">{title}</div>
                    {subtitle ? <div className="mt-1 text-xs text-slate-500">{subtitle}</div> : null}
                </div>
                <div className="px-5 pb-2 divide-y divide-slate-100">{children}</div>
            </div>
        );
    }
    function Line({ label, value }: { label: string; value: React.ReactNode }) {
        return (
            <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white/70 px-4 py-3">
                <div className="text-[12.5px] font-medium text-slate-700">{label}</div>
                <div className="text-[12.5px] text-slate-900">{value}</div>
            </div>
        );
    }

    function Chip({ children }: { children: React.ReactNode }) {
        return (
            <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[12px] text-slate-700">
                {children}
            </span>
        );
    }

    // Demo defaults match your previous previews.
    const [showPaymentSettings, setShowPaymentSettings] = useState(false);

    // Agent seats (dynamic)
    const getAgents = async () => {
        try {
            const response = await axios.get(baseUrl + '/getAgents', { params: { token: currentUser.token, client_uid: client.uid } });
            if (response.data.status === "OK") {
                setAgentSeats(response.data.data.length);
            }
        } catch (e) {
            console.error(e);
        }
    }
    const [agentSeats, setAgentSeats] = useState(0);
    useEffect(() => {
        if (!client || !currentUser || !baseUrl) return;

        getAgents();
    }, [client, currentUser, baseUrl])
    const [agentSeatPrice, setAgentSeatPrice] = useState(0); // NOK per seat / month

    // const [vippsMode] = useState<"Test" | "Live">("Test");
    // const [stripeMode] = useState<"Test" | "Live">("Test");
    // const [walletMode] = useState<"Test" | "Live">("Test");
    // const [smsMode] = useState<"Test" | "Live">("Test");
    const [webrtcMode] = useState<"Test" | "Live">("Test");

    const filterByNumber = (list: any, number: any) =>
        list.find(item => item.number == number)

    const [vippsPrice, setVippsPrice] = useState(0);
    const [stripePrice, setStripePrice] = useState(0);
    const [walletPrice, setWalletPrice] = useState(0);
    const [invoicePrice, setInvoicePrice] = useState(0);
    const [smsPrice, setSmsPrice] = useState(0);
    const [webrtcPrice, setWebrtcPrice] = useState(0);
    const [ivrPrice, setIvrPrice] = useState(0);
    useEffect(() => {
        if (clientPriceLists.length === 0) return;
        let vipps = filterByNumber(clientPriceLists, "1006");
        let stripe = filterByNumber(clientPriceLists, "1005");
        let wallet = filterByNumber(clientPriceLists, "1007");
        let invoice = filterByNumber(clientPriceLists, "1008");
        let sms = filterByNumber(clientPriceLists, "2022");
        let webrtc = filterByNumber(clientPriceLists, "1002");
        let ivr = filterByNumber(clientPriceLists, "1001");
        let agentSeats = filterByNumber(clientPriceLists, "2002");
        setVippsPrice(vipps.price);
        setStripePrice(stripe.price);
        setWalletPrice(wallet.price);
        setInvoicePrice(invoice.price);
        setSmsPrice(sms.price);
        setWebrtcPrice(webrtc.price);
        setIvrPrice(ivr.price);
        setAgentSeatPrice(agentSeats.price);

    }, [clientPriceLists])
    // Billing (demo pricing — replace with your real price list)
    const items: Item[] = useMemo(
        () => [
            {
                id: "vipps",
                group: "Payments",
                title: "Vipps",
                subtitle: "Direct 1:1 payments + login",
                icon: <CreditCard className="h-4.5 w-4.5 text-orange-600" />,
                active: vippsEnabled,
                mode: vippsMode,
                monthly: vippsPrice,
                article_no: 1006,
                notes: ["No split payments", "CSV export for accounting"],
            },
            {
                id: "stripe",
                group: "Payments",
                title: "Stripe",
                subtitle: "Split routing + optional Klarna",
                icon: <CreditCard className="h-4.5 w-4.5 text-blue-700" />,
                active: stripeEnabled,
                mode: stripeMode,
                monthly: stripePrice,
                article_no: 1005,
                notes: ["Supports split payments", "Klarna requires Stripe setup"],
            },
            {
                id: "wallet",
                group: "Payments",
                title: "Wallet",
                subtitle: "Internal wallet ledger",
                icon: <Wallet className="h-4.5 w-4.5 text-slate-800" />,
                active: walletEnabled,
                mode: walletMode,
                monthly: walletPrice,
                article_no: 1007,
                notes: ["Routes 100% to client", "Client reconciles payouts"],
            },
            {
                id: "invoice",
                group: "Payments",
                title: "Invoice",
                subtitle: "Postpaid billing after delivery",
                icon: <Receipt className="h-4.5 w-4.5 text-slate-700" />,
                active: invoiceEnabled,
                mode: "Test",
                monthly: invoicePrice,
                article_no: 1008,
                notes: ["Invoice sent after delivery", "Included in CSV exports"],
            },
            {
                id: "sms",
                group: "Comms",
                title: "SMS services",
                subtitle: "Sender ID + alerts + link delivery",
                icon: <MessageSquare className="h-4.5 w-4.5 text-slate-900" />,
                active: smsEnabled,
                monthly: smsPrice,
                article_no: 2022,
                notes: ["Alphanumeric sender name", "Usage billed separately (provider)"],
            },
            {
                id: "webrtc",
                group: "Comms",
                title: "Video (WebRTC)",
                subtitle: "Direct calls or booking-only",
                icon: <Video className="h-4.5 w-4.5 text-blue-700" />,
                active: videoEnabled,
                mode: webrtcMode,
                monthly: webrtcPrice,
                article_no: 1002,
                notes: ["Booking links supported", "Works with SMS/Email delivery"],
            },
            {
                id: "ivr",
                group: "Comms",
                title: "Audio (IVR)",
                subtitle: "Direct calls or booking-only",
                icon: <PhoneCall className="h-4.5 w-4.5 text-blue-700" />,
                active: audioEnabled,
                monthly: ivrPrice,
                article_no: 1001,
                notes: ["Works with one to one and booking delivery"],
            },
        ],
        [
            audioEnabled,
            videoEnabled,
            smsEnabled,
            walletEnabled,
            stripeEnabled,
            vippsEnabled,
            invoiceEnabled,
            vippsMode,
            stripeMode,
            walletMode,
            webrtcMode,
            vippsPrice,
            stripePrice,
            walletPrice,
            invoicePrice,
        ]
    );

    const activeItems = items.filter((i) => i.active);
    const servicesMonthly = activeItems.reduce((sum, i) => sum + parseFloat(i.monthly), 0);
    const agentSeatsMonthly = agentSeats * agentSeatPrice;
    const totalMonthly = servicesMonthly + agentSeatsMonthly;

    const headerSummary = useMemo(() => {
        const a = activeItems.map((i) => i.title);
        return a.length ? a.join(" · ") : "No services selected";
    }, [activeItems]);

    // Payment settings (demo fields)
    const [cardName, setCardName] = useState("Chamcore Client");
    const [cardNumber, setCardNumber] = useState("4242 4242 4242 4242");
    const [cardExp, setCardExp] = useState("12/28");
    const [cardCvc, setCardCvc] = useState("123");

    function PaymentSettings() {
        return (
            <div className="rounded-2xl border border-slate-200 bg-white/70 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <div className="text-sm font-semibold text-slate-900">Payment settings</div>
                        <div className="mt-1 text-[12.5px] leading-5 text-slate-600">
                            Monthly charges are billed automatically to the client’s payment card.
                        </div>
                    </div>
                    <Badge variant="outline" className="rounded-full">
                        <Lock className="mr-1 h-3.5 w-3.5" />
                        Secure
                    </Badge>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="space-y-2">
                        <label className="text-[12px] font-medium text-slate-700">Name on card</label>
                        <input value={cardName} onChange={(e) => setCardName(e.target.value)} className="flex w-full border border-input px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-10 rounded-xl bg-white" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-[12px] font-medium text-slate-700">Card number</label>
                        <input value={cardNumber} onChange={(e) => setCardNumber(e.target.value)} className="flex w-full border border-input px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-10 rounded-xl bg-white" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-[12px] font-medium text-slate-700">Expiry</label>
                        <input value={cardExp} onChange={(e) => setCardExp(e.target.value)} className="flex w-full border border-input px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-10 rounded-xl bg-white" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-[12px] font-medium text-slate-700">CVC</label>
                        <input value={cardCvc} onChange={(e) => setCardCvc(e.target.value)} className="flex w-full border border-input px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-10 rounded-xl bg-white" />
                    </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                    <Button variant="outline" className="h-10 rounded-xl bg-white">
                        Verify card
                    </Button>
                    <Button className="h-10 rounded-xl">Save billing card</Button>
                </div>

                <div className="mt-3 text-[12px] leading-5 text-slate-600">
                    Tip: In production, use Stripe Setup Intents or a PCI-compliant tokenization flow (no raw card storage).
                </div>
            </div>
        );
    }


    useEffect(() => {
        if (!default_configs) return;

        setVippsEnabled(default_configs.vipps_pay === "yes" ? true : false)
        setStripeEnabled(default_configs.card_payment === "yes" ? true : false);
        setWalletEnabled(default_configs.wallet_payment === "yes" ? true : false);
        setVippsMode(default_configs.vipps_account_mode || "test");
        setStripeMode(client.stripe_account_use || "test");
        setStripeSk(client.stripe_api_key);
        setStripePk(client.stripe_pub_key);
        setVippsClientId(default_configs.vipps_client_id);
        setVippsClientSecret(default_configs.vipps_client_secret);
        setVippsPrimarySubKey(default_configs.vipps_subscription_key);
        setVippsMerchantSerial(default_configs.vipps_msn);
        setTestPhoneNumber(default_configs.test_phone_number);
        setSmsEnabled(default_configs.sms_service === "yes" ? true : false);
        setSmsSenderId(client.brand_name);
        setSmsPayment(default_configs.send_payment_sms === "yes" ? true : false);
        setSmsProvider(client.twilio_account_sid ? "twilio" : "none");
        setSmsChatAlert(default_configs.SMS_customer_chat_alert === "ues" ? true : false);
        setSmsAgentLogin(default_configs.alert_agent_when_available === "yes" ? true : false);
        setSmsAgentNick(default_configs.agent_nick_sms === "yes" ? true : false);
        setTwilioSid(client.twilio_account_sid);
        setTwilioToken(client.twilio_auth_token);
        setVideoEnabled(default_configs.video === "yes" ? true : false);
        setAudioEnabled(!default_configs?.ivr || default_configs?.ivr === "yes" || default_configs?.ivr === "" ? true : false);
        setWalletPayout(default_configs.wallet_payout);
        setWalletService(default_configs.wallet_service);
        setInvoiceEnabled(default_configs.invoice_payment === "yes" ? true : false);
        setInvoiceInputs({
            bank_account: invoice_inputs?.bank_account,
            due_date_days: invoice_inputs?.due_date_days,
            email_body: invoice_inputs?.email_body,
            reminder_body: invoice_inputs?.reminder_body,
            reminder_sent_after: invoice_inputs?.reminder_sent_after,
            invoice_number: invoice_inputs?.invoice_number ? invoice_inputs.invoice_number : 1000,
            email_draft: invoice_inputs?.email_draft,
            draft_generated: invoice_inputs?.draft_generated,
        })
        setState({
            webrtcAudio: default_configs.WebRTC === "yes" ? true : false,
            webinarStreaming: default_configs.webinar === "yes" ? true : false,
            videocall11: default_configs.video === "yes" ? true : false,
            perMinute: default_configs.video_one_to_one_charge_per_minute === "yes" ? true : false,
            perConference: default_configs.video_one_to_one_charge_per_conference === "yes" ? true : false,
            joinWithLink: default_configs.join_with_link === "yes" ? true : false,
        })

    }, [client.default_configs])
    // END Step 3 States And Functions

    const handleOnBackClicked = () => {
        if(localStorage.getItem("supportNavigateFrom") === "Innstillinger"){
            localStorage.removeItem("supportNavigateFrom");
            setActiveTab("Innstillinger");
        }
    }

    return (
        <div
            style={{
                "--primary-color": primaryColor,
                "--secondary-color": secondaryColor,
                "--gradient-bg": useGradient
                    ? `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`
                    : primaryColor,
            } as React.CSSProperties}
        >
            {localStorage.getItem("supportNavigateFrom") === "Innstillinger" && (
                <button
                    onClick={handleOnBackClicked}
                    className="mb-6 text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                    ← Tilbake
                </button>
            )}
            <div className="min-h-screen bg-gradient-to-br from-[#B5D8FF] via-[#D2E8FF] to-[#F4F9FF] text-[#062150] flex flex-col relative">


                {/* Top bar */}
                <header className="border-b border-slate-200 bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF]/80 backdrop-blur z-10">
                    <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-xl bg-[var(--primary-color)]/10 border border-[var(--primary-color)]/50 flex items-center justify-center text-xs font-semibold text-[#062150]">
                                CC
                            </div>
                            <div>
                                <p className="text-sm font-semibold tracking-tight">Chamcore Site Builder</p>
                                <p className="text-xs text-slate-500">Microcomponents · APIs · Live preview</p>
                            </div>
                        </div>

                        {/* Theme Controls */}
                        <div className="top-3 justify-center flex items-center gap-4 bg-white/80 backdrop-blur px-3 py-2 rounded-xl border border-slate-300 z-20 shadow-sm">
                            <div className="flex items-center gap-2">
                                <label className="text-[11px] text-slate-600">Primary</label>
                                <input
                                    type="color"
                                    value={primaryColor || "#0C5FD1"}
                                    onChange={(e) => {
                                        setPrimaryColor(e.target.value)
                                        setAppearance(a => ({ ...a, gradientFrom: e.target.value }))
                                    }}
                                    className="h-6 w-6 rounded cursor-pointer border border-slate-400"
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <label className="text-[11px] text-slate-600">Secondary</label>
                                <input
                                    type="color"
                                    value={secondaryColor || "#f6f6f7"}
                                    onChange={(e) => {
                                        setSecondaryColor(e.target.value)
                                        setAppearance(a => ({ ...a, gradientTo: e.target.value }))
                                    }}
                                    className="h-6 w-6 rounded cursor-pointer border border-slate-400"
                                />
                            </div>
                            <label className="flex items-center gap-1 text-[11px] text-slate-600">
                                <input
                                    type="checkbox"
                                    checked={useGradient}
                                    onChange={(e) => {
                                        setUseGradient(e.target.checked)
                                        setAppearance(a => ({ ...a, gradientEnabled: e.target.checked }))
                                    }
                                    }
                                />
                                Gradient
                            </label>
                        </div>

                        {/* <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1 rounded-full bg-[#E4F0FF] border border-slate-300 px-1 py-0.5 text-xs">
                                <button
                                    className={`px-2 py-1 rounded-full transition text-[11px] ${mode === "blueprint"
                                            ? "bg-[var(--primary-color)] text-white font-semibold"
                                            : "text-[#0C5FD1]"
                                        }`}
                                    onClick={() => setMode("blueprint")}
                                >
                                    Blueprint mode
                                </button>
                                <button
                                    className={`px-2 py-1 rounded-full transition text-[11px] ${mode === "template"
                                            ? "bg-[var(--primary-color)] text-white font-semibold"
                                            : "text-[#0C5FD1]"
                                        }`}
                                    onClick={() => setMode("template")}
                                >
                                    Template mode
                                </button>
                            </div>
                            <button className="text-xs px-3 py-1.5 rounded-full border border-slate-300 hover:border-[var(--primary-color)] hover:text-[#0C5FD1] transition bg-white/70">
                                Preview mobile
                            </button>
                        </div> */}
                    </div>
                </header>

                {/* Stepper */}
                <div className="border-b border-slate-200 bg-[#E4F0FF]/60">
                    <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-3 text-xs">
                            {stepBadges.map((step, index) => {
                                const active = index === stepActive;
                                const complete = index < 0;
                                return (
                                    <div key={step.id} className="flex items-center gap-2">
                                        <div onClick={() => setStepActive(index)}
                                            className={`h-6 px-2 rounded-full border text-[11px] flex items-center gap-1 transition cursor-pointer ${active
                                                ? "border-[var(--primary-color)] bg-[var(--primary-color)]/10 text-[#0C5FD1]"
                                                : "border-slate-300 text-[#0C5FD1] bg-white/60"
                                                }`}
                                        >
                                            <span
                                                className={`h-3 w-3 rounded-full border ${active
                                                    ? "border-[var(--primary-color)] bg-[var(--primary-color)]/70"
                                                    : complete
                                                        ? "border-emerald-400 bg-emerald-500/60"
                                                        : "border-slate-400 bg-[#79AFFF]"
                                                    }`}
                                            />
                                            <span className="uppercase tracking-wide">Step {step.id}</span>
                                            <span className="hidden sm:inline">· {step.label}</span>
                                        </div>
                                        {index < stepBadges.length - 1 && (
                                            <span className="text-[#0A4A99]">···</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="hidden sm:flex items-center gap-2 text-[11px] text-slate-500">
                            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                            Live microservice bindings ready
                        </div>
                    </div>
                </div>

                {/* Main layout */}
                <div className="flex-1 max-w-7xl mx-auto w-full px-4 py-4 flex flex-col lg:flex-row gap-4">
                    {stepActive === 0 && (
                        <>
                            {/* Left: block selector */}
                            <aside className="w-full lg:w-72 shrink-0 h-[calc(100vh-160px)] sticky top-[160px] overflow-y-auto rounded-2xl border border-slate-200 bg-[#E4F0FF]/80 p-3 flex flex-col gap-3">
                                <div>
                                    <p className="text-xs font-semibold text-[#0A4A99] mb-1">Microcomponent library</p>
                                    <p className="text-[11px] text-slate-500 mb-2">
                                        Tick the blocks you want. Chamcore wires the APIs, data and payments under the hood.
                                    </p>
                                </div>

                                <div className="space-y-3 overflow-y-auto pr-1 max-h-[calc(100vh-210px)]">
                                    {/* Hero options */}
                                    <SectionHeader title="Hero" subtitle="First impression above the fold" />
                                    <div className="space-y-1">
                                        {blockOptions.hero.map((option) => (
                                            <BlockToggle
                                                key={option.id}
                                                category="hero"
                                                option={option}
                                                selected={isSelected("hero", option.id)}
                                                onToggle={handleSelect}
                                            />
                                        ))}
                                    </div>

                                    {/* Wallet */}
                                    {blockOptions?.wallet && (
                                        <>
                                            <SectionHeader title="Wallet" subtitle="How your wallet system shows up" />
                                            <div className="space-y-1">
                                                {blockOptions?.wallet?.map((option) => (
                                                    <BlockToggle
                                                        key={option.id}
                                                        category="wallet"
                                                        option={option}
                                                        selected={isSelected("wallet", option.id)}
                                                        onToggle={handleSelect}
                                                    />
                                                ))}
                                            </div>
                                        </>
                                    )}
                                    {/* Agents */}
                                    <SectionHeader title="Agents" subtitle="How your team shows up" />
                                    <div className="space-y-1">
                                        {blockOptions.agents.map((option) => (
                                            <BlockToggle
                                                key={option.id}
                                                category="agents"
                                                option={option}
                                                selected={isSelected("agents", option.id)}
                                                onToggle={handleSelect}
                                            />
                                        ))}
                                    </div>
                                    {/* Events */}
                                    <SectionHeader title="Events" subtitle="How your events show up" />
                                    <div className="space-y-1">
                                        {blockOptions.events.map((option) => (
                                            <BlockToggle
                                                key={option.id}
                                                category="events"
                                                option={option}
                                                selected={isSelected("events", option.id)}
                                                onToggle={handleSelect}
                                            />
                                        ))}
                                    </div>
                                    {/* Skills */}
                                    <SectionHeader title="Skills" subtitle="How your skills show up" />
                                    <div className="space-y-1">
                                        {blockOptions.skills.map((option) => (
                                            <BlockToggle
                                                key={option.id}
                                                category="skills"
                                                option={option}
                                                selected={isSelected("skills", option.id)}
                                                onToggle={handleSelect}
                                            />
                                        ))}
                                    </div>

                                    {/* Blog */}
                                    <SectionHeader title="Blog" subtitle="Content from your CMS" />
                                    <div className="space-y-1">
                                        {blockOptions.blog.map((option) => (
                                            <BlockToggle
                                                key={option.id}
                                                category="blog"
                                                option={option}
                                                selected={isSelected("blog", option.id)}
                                                onToggle={handleSelect}
                                            />
                                        ))}
                                    </div>

                                    {/* Video */}
                                    <SectionHeader title="Video & streaming" subtitle="Chamcore video + IVR" />
                                    <div className="space-y-1">
                                        {blockOptions.video.map((option) => (
                                            <BlockToggle
                                                key={option.id}
                                                category="video"
                                                option={option}
                                                selected={isSelected("video", option.id)}
                                                onToggle={handleSelect}
                                            />
                                        ))}
                                    </div>

                                    {/* CTA */}
                                    <SectionHeader title="Call-to-action" subtitle="Last push before footer" />
                                    <div className="space-y-1 mb-2">
                                        {blockOptions.cta.map((option) => (
                                            <BlockToggle
                                                key={option.id}
                                                category="cta"
                                                option={option}
                                                selected={isSelected("cta", option.id)}
                                                onToggle={handleSelect}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </aside>

                            {/* Right: live preview */}
                            <main className="flex-1 rounded-2xl border border-slate-200 bg-white/80 flex flex-col shadow-[0_18px_40px_rgba(15,23,42,0.12)]">
                                <div className="border-b border-slate-200 px-4 py-2 flex items-center justify-between text-[11px] text-slate-500 bg-[#F4F8FF]">
                                    <div className="flex items-center gap-2">
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-300 bg-white">
                                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                                            Live preview
                                        </span>
                                        <span>Client: demo.chamcore.app</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button onClick={() => setViewport("desktop")} className={`px-2 py-0.5 rounded-full border ${viewport === "desktop" ? "border-[var(--primary-color)]" : "border-slate-300"} hover:border-[var(--primary-color)] hover:text-[#0C5FD1] transition bg-white/80`}>
                                            Desktop
                                        </button>
                                        <button onClick={() => setViewport("tablet")} className={`px-2 py-0.5 rounded-full border ${viewport === "tablet" ? "border-[var(--primary-color)]" : "border-slate-300"} hover:border-[var(--primary-color)] hover:text-[#0C5FD1] transition bg-white/80`}>
                                            Tablet
                                        </button>
                                        <button onClick={() => setViewport("mobile")} className={`px-2 py-0.5 rounded-full border ${viewport === "mobile" ? "border-[var(--primary-color)]" : "border-slate-300"} hover:border-[var(--primary-color)] hover:text-[#0C5FD1] transition bg-white/80`}>
                                            Mobile
                                        </button>
                                    </div>
                                </div>

                                <div className="flex-1 bg-[var(--gradient-bg)]/10 overflow-y-auto">
                                    <div className={`w-full ${viewport === "desktop" ? "max-w-4xl" : viewport === "tablet" ? "max-w-xl" : "max-w-sm"} mx-auto p-4 space-y-6`}>
                                        {/* HERO */}
                                        {selectedBlocks.hero === "heroA" && <TeletorgetHero onRegisterClick={onRegisterClick} baseUrl={baseUrl} handleChipClick={handleChipClick} from={"support-client"} setHeroData={setHeroData} heroData={heroData} />}
                                        {selectedBlocks.hero === "heroB" && <HeroB />}
                                        {selectedBlocks.hero === "heroC" && <HeroC />}

                                        {/* WALLET */}
                                        {selectedBlocks.wallet === "walletA" && <BuyMinutesSection onTopUp={() => console.log("e")} T={T} baseUrl={baseUrl} from={"support-client"} setHeroData={setHeroData} wallet={heroData.wallet} />}
                                        {/* AGENTS */}
                                        {selectedBlocks.agents === "agentsA" && <Agents baseUrl={baseUrl} T={T} currentUser={currentUser} onLoginClick={onLoginClick} onLoginSuccess={onLoginSuccess} searchField={searchField} setSearchField={setSearchField} onSearchClick={onSearchClick} filteredAdvisors={filteredAdvisors} from={'support-client'} />}
                                        {selectedBlocks.agents === "agentsB" && <AgentsList />}
                                        {/* EVENTS */}
                                        {selectedBlocks.events === "eventsA" && client && <EventModules from={'support-client'} appearance={appearance} setAppearance={setAppearance} baseUrl={baseUrl} currentUser={currentUser} setHeroData={setHeroData} events={heroData.events} />}
                                        {/* SKILLS */}
                                        {selectedBlocks.skills === "skillsA" && client && <FestivalAds T={T} client={client} baseUrl={baseUrl} handleChipClick={handleChipClick} from={'support-client'} />}

                                        {/* BLOG */}
                                        {selectedBlocks.blog === "blogA" && <Blogs T={T} from={"support-client"} baseUrl={baseUrl} client={client} limit={"6"} />}
                                        {selectedBlocks.blog === "blogB" && <BlogList />}

                                        {/* VIDEO */}
                                        {selectedBlocks.video === "videoA" && <VideoLivestream />}
                                        {selectedBlocks.video === "videoB" && <VideoLibrary />}

                                        {/* CTA */}
                                        {selectedBlocks.cta === "ctaA" && <CtaSimple />}
                                        {selectedBlocks.cta === "ctaB" && <CtaMulti />}

                                        {/* Next step card */}
                                        <div className="mt-6 rounded-2xl border border-slate-200 bg-[#E4F0FF] px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                            <div>
                                                <p className="text-sm font-semibold text-[#083570]">Next: connect Chamcore services</p>
                                                <p className="text-xs text-slate-500 max-w-xl mt-1">
                                                    When you hit <span className="font-semibold text-[#0C5FD1]">Save & continue</span>, we take you to
                                                    channel setup: SMS sender ID, email domains, IVR numbers, wallets and payment rails.
                                                </p>
                                            </div>
                                            <button onClick={handleSaveAndContinueClick} className="inline-flex items-center gap-1 rounded-full bg-[var(--primary-color)] text-white text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">
                                                Save & continue
                                                <span>→</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </main>
                        </>
                    )}

                    {stepActive === 1 && (
                        <div className="">
                            <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.35 }}
                                className="rounded-3xl border border-slate-200 p-5 shadow-sm"
                                style={{ backgroundImage: "linear-gradient(to right, var(--primary-color), var(--secondary-color))" }}
                            >
                                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <Pill>
                                                <Shield className="mr-1.5 h-3.5 w-3.5" />
                                                Secure onboarding
                                            </Pill>
                                            <Pill>
                                                <CreditCard className="mr-1.5 h-3.5 w-3.5" />
                                                Payments
                                            </Pill>
                                            <Pill>
                                                <Wallet className="mr-1.5 h-3.5 w-3.5" />
                                                Wallet ledger
                                            </Pill>
                                        </div>
                                        <h1 className="mt-3 text-2xl font-bold tracking-tight text-slate-900">Connect channels</h1>
                                        <p className="mt-1 max-w-3xl text-[13.5px] leading-6 text-slate-600">
                                            Simple table view: enable the channel, then open Details to add keys and choose Test/Live for safe
                                            testing.
                                        </p>
                                    </div>
                                </div>
                            </motion.div>

                            <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-[1fr_340px]">
                                <div className="space-y-3">
                                    <Card className="rounded-2xl border-slate-200 bg-white/70 shadow-sm">
                                        <CardHeader className="pb-3">
                                            <CardTitle className="text-base">Channels</CardTitle>
                                            <p className="text-[12.5px] leading-5 text-slate-600">
                                                Turn on what you need now. Use Details to configure keys and run in Test mode first.
                                            </p>
                                        </CardHeader>
                                        <CardContent className="space-y-3">
                                            <Row
                                                id="vipps"
                                                icon={<CreditCard className="h-5 w-5 text-orange-600" />}
                                                title="Vipps"
                                                tag={<Badge className="rounded-full bg-orange-600">1:1</Badge>}
                                                desc="Direct 1:1 payments + customer registration/login. No split payments."
                                                enabled={vippsEnabled}
                                                setEnabled={setVippsEnabled}
                                                warning={
                                                    <Notice tone="warn" title="Vipps: no split payments">
                                                        Payment is routed in full to the client. Revenue splits must be handled by the client (outside
                                                        Vipps). Chamcore provides CSV exports for reconciliation.
                                                    </Notice>
                                                }
                                            />

                                            <Row
                                                id="stripe"
                                                icon={<CreditCard className="h-5 w-5 text-blue-700" />}
                                                title="Stripe"
                                                tag={<Badge className="rounded-full bg-blue-600">Split</Badge>}
                                                desc="Split payments + routing to multiple recipients. Optional Klarna if enabled in Stripe."
                                                enabled={stripeEnabled}
                                                setEnabled={setStripeEnabled}
                                            />

                                            <Row
                                                id="wallet"
                                                icon={<Wallet className="h-5 w-5 text-slate-800" />}
                                                title="Wallet"
                                                tag={<Badge variant="outline" className="rounded-full">Internal</Badge>}
                                                desc="Internal wallet using Vipps/Stripe rails. Prepaid minutes/consultations route to client in full."
                                                enabled={walletEnabled}
                                                setEnabled={setWalletEnabled}
                                                warning={
                                                    <Notice tone="warn" title="Wallet: client settles payouts">
                                                        Wallet purchases are routed in full to the client. Client reconciles delivered minutes/value and
                                                        payouts to agents. Chamcore tracks balances + usage and exports CSV.
                                                    </Notice>
                                                }
                                            />
                                            <Row
                                                id="invoice"
                                                icon={<Receipt className="h-5 w-5 text-slate-800" />}
                                                title="Invoice"
                                                tag={<Badge className="rounded-full bg-blue-600">Split</Badge>}
                                                desc="Internal wallet using Vipps/Stripe rails. Prepaid minutes/consultations route to client in full."
                                                enabled={invoiceEnabled}
                                                setEnabled={setInvoiceEnabled}
                                                warning={
                                                    <Notice tone="warn" title="Invoice: client settles payouts">
                                                        Invoice payment + routing to multiple recipients Optional Klarna if enabled in Stripe.
                                                    </Notice>
                                                }
                                            />

                                            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                                                <div className="flex items-center gap-2 sm:hidden">
                                                    <label className="text-[12px] text-slate-600">Enable</label>
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[12px] text-slate-600">Vipps</span>
                                                            <Switch checked={vippsEnabled} onCheckedChange={setVippsEnabled} />
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[12px] text-slate-600">Stripe</span>
                                                            <Switch checked={stripeEnabled} onCheckedChange={setStripeEnabled} />
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[12px] text-slate-600">Wallet</span>
                                                            <Switch checked={walletEnabled} onCheckedChange={setWalletEnabled} />
                                                        </div>
                                                    </div>
                                                </div>
                                                <button className="inline-flex items-center gap-1 rounded-full justify-between bg-white text-[var(--primary-color)] border border-[var(--primary-color)] text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">
                                                    View CSV format
                                                </button>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>

                                <div className="space-y-4">
                                    <Card className="rounded-2xl border-slate-200 bg-white/70 shadow-sm">
                                        <CardHeader className="pb-3">
                                            <CardTitle className="text-base">Testing checklist</CardTitle>
                                        </CardHeader>
                                        <CardContent className="space-y-3 text-[12.5px] leading-5 text-slate-700">
                                            <div className="rounded-xl border border-slate-200 bg-white/70 p-3">
                                                <div className="text-[12px] font-semibold text-slate-800">1) Start in Test mode</div>
                                                <div className="mt-1 text-slate-600">Validate payments, callbacks, and CSV export before Live.</div>
                                            </div>
                                            <div className="rounded-xl border border-slate-200 bg-white/70 p-3">
                                                <div className="text-[12px] font-semibold text-slate-800">2) Validate credentials</div>
                                                <div className="mt-1 text-slate-600">Use “Validate” inside Details to confirm keys.</div>
                                            </div>
                                            <div className="rounded-xl border border-slate-200 bg-white/70 p-3">
                                                <div className="text-[12px] font-semibold text-slate-800">3) Switch to Live</div>
                                                <div className="mt-1 text-slate-600">Only when client is ready to take real payments.</div>
                                            </div>
                                        </CardContent>
                                    </Card>

                                    <Card className="rounded-2xl border-slate-200 bg-white/70 shadow-sm">
                                        <CardHeader className="pb-3">
                                            <CardTitle className="text-base">Quick links</CardTitle>
                                        </CardHeader>
                                        <CardContent className="grid gap-2">
                                            <button className="inline-flex items-center gap-1 rounded-full justify-between bg-white text-[var(--primary-color)] border border-[var(--primary-color)] text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">
                                                Create Vipps business account <ExternalLink className="h-4 w-4" />
                                            </button>
                                            <button className="inline-flex items-center gap-1 rounded-full justify-between bg-white text-[var(--primary-color)] border border-[var(--primary-color)] text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">
                                                Create Stripe business account <ExternalLink className="h-4 w-4" />
                                            </button>
                                            <button className="inline-flex items-center gap-1 rounded-full justify-between bg-white text-[var(--primary-color)] border border-[var(--primary-color)] text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">
                                                Stripe split payment terms <ExternalLink className="h-4 w-4" />
                                            </button>
                                        </CardContent>
                                    </Card>
                                </div>
                            </div>



                            {/* Hero */}
                            <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.35 }}
                                className="rounded-3xl border border-slate-200 p-5 shadow-sm mt-4"
                                style={{ backgroundImage: "linear-gradient(to right, var(--primary-color), var(--secondary-color))" }}
                            >
                                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <Pill>
                                                <Shield className="mr-1.5 h-3.5 w-3.5" />
                                                Secure delivery
                                            </Pill>
                                            <Pill>
                                                <MessageSquare className="mr-1.5 h-3.5 w-3.5" />
                                                SMS
                                            </Pill>
                                            <Pill>
                                                <Video className="mr-1.5 h-3.5 w-3.5" />
                                                WebRTC
                                            </Pill>
                                            <Pill>
                                                <Phone className="mr-1.5 h-3.5 w-3.5" />
                                                Booking links
                                            </Pill>
                                        </div>
                                        <h1 className="mt-3 text-2xl font-bold tracking-tight text-slate-900">Messaging & video services</h1>
                                        <p className="mt-1 max-w-3xl text-[13.5px] leading-6 text-slate-600">
                                            Enable SMS and WebRTC. Use Details to set Test/Live, configure sender name and provider keys, and
                                            control whether calls are direct or booking-only.
                                        </p>
                                    </div>
                                </div>
                            </motion.div>

                            <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-[1fr_340px]">
                                {/* Left */}
                                <div className="space-y-3">
                                    <Card className="rounded-2xl border-slate-200 bg-white/70 shadow-sm">
                                        <CardHeader className="pb-3">
                                            <CardTitle className="text-base">Services</CardTitle>
                                            <p className="text-[12.5px] leading-5 text-slate-600">
                                                Turn on the service. Use Details to configure sender name, provider keys, and call rules.
                                            </p>
                                        </CardHeader>
                                        <CardContent className="space-y-3">
                                            {/* SMS */}
                                            <ServiceRow
                                                id="sms"
                                                icon={<MessageSquare className="h-5 w-5 text-slate-900" />}
                                                title="SMS services"
                                                tag={<Badge className="rounded-full bg-slate-900">Messaging</Badge>}
                                                desc="Payment SMS, chat alerts, login alerts + link delivery. Sender name supported (alphanumeric)."
                                                enabled={smsEnabled}
                                                setEnabled={setSmsEnabled}
                                                open={openSmsOrVideo}
                                                setOpen={setOpenSmsOrVideo}
                                            >
                                                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">

                                                    <div className="lg:col-span-2 space-y-3">
                                                        <Notice tone="info" title="Sender name (alphanumeric)">
                                                            Sender is shown to the customer when supported by the network. We enforce A–Z and 0–9 and a
                                                            max length of 11 characters.
                                                        </Notice>

                                                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                                            <MiniField
                                                                label="SMS Sender name"
                                                                value={smsSenderId}
                                                                onChange={(v) => setSmsSenderId(sanitizeSenderId(v))}
                                                                placeholder="CHAMCORE"
                                                                helper="Example: CHAMCORE, TELETORGET, NETTSIDEN"
                                                            />
                                                            <div className="space-y-2">
                                                                <label className="text-[12px] font-medium text-slate-700">SMS provider</label>
                                                                <Select value={smsProvider} onValueChange={(v) => setSmsProvider(v as any)}>
                                                                    <SelectTrigger className="h-10 rounded-xl bg-white">
                                                                        <SelectValue placeholder="Select" />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value="twilio">Twilio</SelectItem>
                                                                        <SelectItem value="sonetel">Sonetel</SelectItem>
                                                                        <SelectItem value="none">Other / Later</SelectItem>
                                                                    </SelectContent>
                                                                </Select>
                                                                <p className="text-[12px] leading-5 text-slate-600">Choose provider per tenant.</p>
                                                            </div>
                                                        </div>

                                                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                                            <ToggleLine
                                                                title="Send payment SMS"
                                                                description="Receipts + payment links via SMS."
                                                                checked={smsPayment}
                                                                onChange={setSmsPayment}
                                                            />
                                                            <ToggleLine
                                                                title="SMS customer chat alert"
                                                                description="Notify customers about new chat messages."
                                                                checked={smsChatAlert}
                                                                onChange={setSmsChatAlert}
                                                            />
                                                            <ToggleLine
                                                                title="Send SMS when agent login"
                                                                description="Optional notification when agent becomes available."
                                                                checked={smsAgentLogin}
                                                                onChange={setSmsAgentLogin}
                                                            />
                                                            <ToggleLine
                                                                title="Send agent nick info"
                                                                description="When enabled, Chamcore will send agent nick by SMS."
                                                                checked={smsAgentNick}
                                                                onChange={setSmsAgentNick}
                                                            />
                                                        </div>

                                                        {smsProvider === "twilio" ? (
                                                            <div className="rounded-2xl border border-slate-200 bg-white/70 p-4">
                                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                                    <div className="text-[12px] font-semibold text-slate-800">Twilio credentials</div>
                                                                    <a
                                                                        href="#"
                                                                        className="inline-flex items-center gap-1 text-[12px] font-medium text-[var(--primary-color)] hover:underline"
                                                                    >
                                                                        Create Twilio account <ExternalLink className="h-3.5 w-3.5" />
                                                                    </a>
                                                                </div>
                                                                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                                                    <MiniField label="Account SID" value={twilioSid} onChange={setTwilioSid} />
                                                                    <MiniField label="Auth token" value={twilioToken} onChange={setTwilioToken} sensitive />
                                                                </div>
                                                                <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                                                                    <button className="inline-flex items-center gap-1 border border-[var(--primary-color)] rounded-full bg-[var(--secondary-color)] text-dark text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">
                                                                        Send test SMS
                                                                    </button>
                                                                    <button className="inline-flex items-center gap-1 rounded-full bg-[var(--primary-color)] text-white text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">Save SMS</button>
                                                                </div>
                                                            </div>
                                                        ) : null}

                                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                                            <button className="inline-flex items-center gap-1 border border-[var(--primary-color)] rounded-full bg-[var(--secondary-color)] text-dark text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">
                                                                View CSV mapping
                                                            </button>
                                                            <button className="inline-flex items-center gap-1 rounded-full bg-[var(--primary-color)] text-white text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">Activate SMS binding</button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </ServiceRow>

                                            {/* Video */}
                                            <ServiceRow
                                                id="video"
                                                icon={<Video className="h-5 w-5 text-blue-700" />}
                                                title="Video services"
                                                tag={<Badge className="rounded-full bg-blue-600">WebRTC</Badge>}
                                                desc="Enable WebRTC video. Choose direct calls or booking-only, and send join-links to the customer."
                                                enabled={videoEnabled}
                                                setEnabled={setVideoEnabled}
                                                open={openSmsOrVideo}
                                                setOpen={setOpenSmsOrVideo}
                                            >
                                                <div className="mb-6 mt-3 grid grid-cols-1 lg:grid-cols-2 gap-5">
                                                    <Section title="Core capabilities" subtitle="Core video and audio services.">
                                                        <OptionRow
                                                            title="WebRTC 1:1 Audio"
                                                            description="1:1 audio via WebRTC."
                                                            value={state.webrtcAudio}
                                                            onChange={(v) => set("webrtcAudio", v)}
                                                            disabled={lockedByLinkOnly}
                                                            note={lockedByLinkOnly ? "Disabled by policy" : undefined}
                                                        />
                                                        <OptionRow
                                                            title="Webinar – Streaming"
                                                            description="One‑to‑many streaming."
                                                            value={state.webinarStreaming}
                                                            onChange={(v) => set("webinarStreaming", v)}
                                                        />
                                                        <OptionRow
                                                            title="Videocall 1:1"
                                                            description="Allow direct 1:1 video calls."
                                                            value={state.videocall11}
                                                            onChange={(v) => set("videocall11", v)}
                                                            disabled={lockedByLinkOnly}
                                                            note={lockedByLinkOnly ? "Disabled by policy" : undefined}
                                                        />
                                                    </Section>

                                                    <Section title="Access & charging" subtitle="Access and billing rules.">
                                                        <OptionRow
                                                            title="Join with link"
                                                            description="Join only via invite link."
                                                            value={state.joinWithLink}
                                                            onChange={(v) => set("joinWithLink", v)}
                                                            note={state.joinWithLink ? "Policy on" : undefined}
                                                        />
                                                        <OptionRow
                                                            title="1:1 Video Call: charge per minute"
                                                            description="Charge per minute."
                                                            value={state.perMinute}
                                                            onChange={(v) => set("perMinute", v)}
                                                        />
                                                        <OptionRow
                                                            title="1:1 Video Call: charge per conference"
                                                            description="Charge per session."
                                                            value={state.perConference}
                                                            onChange={(v) => set("perConference", v)}
                                                        />
                                                    </Section>
                                                </div>
                                                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">

                                                    <div className="lg:col-span-2 space-y-3">

                                                        <div className="rounded-2xl border border-slate-200 bg-white/70 p-4">
                                                            <div className="flex items-start justify-between gap-4">
                                                                <div className="min-w-0">
                                                                    <div className="text-sm font-semibold text-slate-900">Link delivery</div>
                                                                    <div className="mt-1 text-[12.5px] leading-5 text-slate-600">
                                                                        When booking-only is enabled, Chamcore can automatically send the join-link to the
                                                                        customer.
                                                                    </div>
                                                                </div>
                                                                <Switch checked={sendLinkToCustomer} onCheckedChange={setSendLinkToCustomer} />
                                                            </div>

                                                            {sendLinkToCustomer ? (
                                                                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                                                    <div className="space-y-2">
                                                                        <label className="text-[12px] font-medium text-slate-700">Send link via</label>
                                                                        <Select value={sendLinkVia} onValueChange={(v) => setSendLinkVia(v as any)}>
                                                                            <SelectTrigger className="h-10 rounded-xl bg-white">
                                                                                <SelectValue placeholder="Select" />
                                                                            </SelectTrigger>
                                                                            <SelectContent>
                                                                                <SelectItem value="sms">SMS</SelectItem>
                                                                                <SelectItem value="email">Email</SelectItem>
                                                                                <SelectItem value="both">SMS + Email</SelectItem>
                                                                            </SelectContent>
                                                                        </Select>
                                                                        <p className="text-[12px] leading-5 text-slate-600">
                                                                            SMS requires SMS services enabled.
                                                                        </p>
                                                                    </div>

                                                                    <Notice tone="info" title="Best practice">
                                                                        For clinics: booking-only + link delivery reduces no-shows and keeps the queue clean.
                                                                    </Notice>
                                                                </div>
                                                            ) : null}
                                                        </div>

                                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                                            <button className="inline-flex items-center gap-1 border border-[var(--primary-color)] rounded-full bg-[var(--secondary-color)] text-dark text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">
                                                                Test video room
                                                            </button>
                                                            <button className="h-10 inline-flex items-center gap-1 rounded-full bg-[var(--primary-color)] text-white text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">Activate WebRTC</button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </ServiceRow>
                                            {/* Video */}
                                            <ServiceRow
                                                id="audio"
                                                icon={<PhoneCall className="h-5 w-5 text-blue-700" />}
                                                title="Audio services"
                                                tag={<Badge className="rounded-full bg-blue-600">IVR</Badge>}
                                                desc="Enable IVR audio. Choose direct calls or booking-only to the customer."
                                                enabled={audioEnabled}
                                                setEnabled={setAudioEnabled}
                                                open={openSmsOrVideo}
                                                setOpen={setOpenSmsOrVideo}
                                            >
                                                <div className="mb-6 mt-3 grid grid-cols-1 lg:grid-cols-2 gap-5">
                                                    <Section title="Core capabilities" subtitle="Core video and audio services.">
                                                        <OptionRow
                                                            title="WebRTC 1:1 Audio"
                                                            description="1:1 audio via WebRTC."
                                                            value={state.webrtcAudio}
                                                            onChange={(v) => set("webrtcAudio", v)}
                                                            disabled={lockedByLinkOnly}
                                                            note={lockedByLinkOnly ? "Disabled by policy" : undefined}
                                                        />
                                                        <OptionRow
                                                            title="Webinar – Streaming"
                                                            description="One‑to‑many streaming."
                                                            value={state.webinarStreaming}
                                                            onChange={(v) => set("webinarStreaming", v)}
                                                        />
                                                        <OptionRow
                                                            title="Videocall 1:1"
                                                            description="Allow direct 1:1 video calls."
                                                            value={state.videocall11}
                                                            onChange={(v) => set("videocall11", v)}
                                                            disabled={lockedByLinkOnly}
                                                            note={lockedByLinkOnly ? "Disabled by policy" : undefined}
                                                        />
                                                    </Section>

                                                    <Section title="Access & charging" subtitle="Access and billing rules.">
                                                        <OptionRow
                                                            title="Join with link"
                                                            description="Join only via invite link."
                                                            value={state.joinWithLink}
                                                            onChange={(v) => set("joinWithLink", v)}
                                                            note={state.joinWithLink ? "Policy on" : undefined}
                                                        />
                                                        <OptionRow
                                                            title="1:1 Video Call: charge per minute"
                                                            description="Charge per minute."
                                                            value={state.perMinute}
                                                            onChange={(v) => set("perMinute", v)}
                                                        />
                                                        <OptionRow
                                                            title="1:1 Video Call: charge per conference"
                                                            description="Charge per session."
                                                            value={state.perConference}
                                                            onChange={(v) => set("perConference", v)}
                                                        />
                                                    </Section>
                                                </div>
                                                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">

                                                    <div className="lg:col-span-2 space-y-3">

                                                        <div className="rounded-2xl border border-slate-200 bg-white/70 p-4">
                                                            <div className="flex items-start justify-between gap-4">
                                                                <div className="min-w-0">
                                                                    <div className="text-sm font-semibold text-slate-900">Link delivery</div>
                                                                    <div className="mt-1 text-[12.5px] leading-5 text-slate-600">
                                                                        When booking-only is enabled, Chamcore can automatically send the join-link to the
                                                                        customer.
                                                                    </div>
                                                                </div>
                                                                <Switch checked={sendLinkToCustomer} onCheckedChange={setSendLinkToCustomer} />
                                                            </div>

                                                            {sendLinkToCustomer ? (
                                                                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                                                    <div className="space-y-2">
                                                                        <label className="text-[12px] font-medium text-slate-700">Send link via</label>
                                                                        <Select value={sendLinkVia} onValueChange={(v) => setSendLinkVia(v as any)}>
                                                                            <SelectTrigger className="h-10 rounded-xl bg-white">
                                                                                <SelectValue placeholder="Select" />
                                                                            </SelectTrigger>
                                                                            <SelectContent>
                                                                                <SelectItem value="sms">SMS</SelectItem>
                                                                                <SelectItem value="email">Email</SelectItem>
                                                                                <SelectItem value="both">SMS + Email</SelectItem>
                                                                            </SelectContent>
                                                                        </Select>
                                                                        <p className="text-[12px] leading-5 text-slate-600">
                                                                            SMS requires SMS services enabled.
                                                                        </p>
                                                                    </div>

                                                                    <Notice tone="info" title="Best practice">
                                                                        For clinics: booking-only + link delivery reduces no-shows and keeps the queue clean.
                                                                    </Notice>
                                                                </div>
                                                            ) : null}
                                                        </div>

                                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                                            <button className="inline-flex items-center gap-1 border border-[var(--primary-color)] rounded-full bg-[var(--secondary-color)] text-dark text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">
                                                                Test video room
                                                            </button>
                                                            <button className="h-10 inline-flex items-center gap-1 rounded-full bg-[var(--primary-color)] text-white text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">Activate WebRTC</button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </ServiceRow>

                                            {/* Mobile quick toggles */}
                                            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                                                <div className="flex items-center gap-2 sm:hidden">
                                                    <label className="text-[12px] text-slate-600">Enable</label>
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[12px] text-slate-600">SMS</span>
                                                            <Switch checked={smsEnabled} onCheckedChange={setSmsEnabled} />
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[12px] text-slate-600">WebRTC</span>
                                                            <Switch checked={videoEnabled} onCheckedChange={setVideoEnabled} />
                                                        </div>
                                                    </div>
                                                </div>
                                                <button className="inline-flex items-center gap-1 rounded-full justify-between bg-white text-[var(--primary-color)] border border-[var(--primary-color)] text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">
                                                    Help & docs
                                                </button>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>

                                {/* Right */}
                                <div className="space-y-4">
                                    <Card className="rounded-2xl border-slate-200 bg-white/70 shadow-sm">
                                        <CardHeader className="pb-3">
                                            <CardTitle className="text-base">Testing checklist</CardTitle>
                                        </CardHeader>
                                        <CardContent className="space-y-3 text-[12.5px] leading-5 text-slate-700">
                                            <div className="rounded-xl border border-slate-200 bg-white/70 p-3">
                                                <div className="text-[12px] font-semibold text-slate-800">1) Set Test mode</div>
                                                <div className="mt-1 text-slate-600">Use Test to verify delivery and call flows.</div>
                                            </div>
                                            <div className="rounded-xl border border-slate-200 bg-white/70 p-3">
                                                <div className="text-[12px] font-semibold text-slate-800">2) Send a test SMS</div>
                                                <div className="mt-1 text-slate-600">Confirm sender name and routing is correct.</div>
                                            </div>
                                            <div className="rounded-xl border border-slate-200 bg-white/70 p-3">
                                                <div className="text-[12px] font-semibold text-slate-800">3) Test video link</div>
                                                <div className="mt-1 text-slate-600">Booking-only should generate a join-link and notify customer.</div>
                                            </div>
                                        </CardContent>
                                    </Card>

                                    <Card className="rounded-2xl border-slate-200 bg-white/70 shadow-sm">
                                        <CardHeader className="pb-3">
                                            <CardTitle className="text-base">Quick links</CardTitle>
                                        </CardHeader>
                                        <CardContent className="grid gap-2">
                                            <button className="inline-flex items-center gap-1 rounded-full justify-between bg-white text-[var(--primary-color)] border border-[var(--primary-color)] text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">
                                                Sender ID rules <ExternalLink className="h-4 w-4" />
                                            </button>
                                            <button className="inline-flex items-center gap-1 rounded-full justify-between bg-white text-[var(--primary-color)] border border-[var(--primary-color)] text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">
                                                Twilio messaging docs <ExternalLink className="h-4 w-4" />
                                            </button>
                                            <button className="inline-flex items-center gap-1 rounded-full justify-between bg-white text-[var(--primary-color)] border border-[var(--primary-color)] text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">
                                                WebRTC setup docs <ExternalLink className="h-4 w-4" />
                                            </button>
                                        </CardContent>
                                    </Card>
                                </div>
                            </div>

                            {/* Sticky action bar */}
                            <div className="mt-5 sticky bottom-3 z-20">
                                <div className="rounded-2xl border border-slate-200 bg-white/80 p-3 shadow-lg backdrop-blur">
                                    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <Badge variant="outline" className="rounded-full">
                                                Active: {summaryPayments}
                                            </Badge>
                                            <Badge variant="outline" className="rounded-full">
                                                CSV exports: Enabled
                                            </Badge>
                                            <Badge variant="outline" className="rounded-full">
                                                Default mode: Test (safe)
                                            </Badge>
                                            <Badge variant="outline" className="rounded-full">
                                                Active: {summarySmsAndVideo}
                                            </Badge>
                                            <Badge variant="outline" className="rounded-full">
                                                Default mode: Test (safe)
                                            </Badge>
                                            <Badge variant="outline" className="rounded-full">
                                                Links: {sendLinkToCustomer ? `On (${sendLinkVia})` : "Off"}
                                            </Badge>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button className="h-10 inline-flex items-center gap-1 rounded-full bg-[var(--secondary-color)] text-[var(--primary-color)] text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">
                                                Back
                                            </button>
                                            <button onClick={handleStepTwoSaveAndContinueClick} className="h-10 inline-flex items-center gap-1 rounded-full bg-[var(--primary-color)] text-white text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">Save & Continue</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {stepActive === 2 && (
                        <div>
                            {/* Hero */}
                            <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.35 }}
                                className="rounded-3xl border border-slate-200 p-5 shadow-sm"
                                style={{ backgroundImage: "linear-gradient(to right, var(--primary-color), var(--secondary-color))" }}
                            >
                                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <Pill>
                                                <Shield className="mr-1.5 h-3.5 w-3.5" />
                                                Secure onboarding
                                            </Pill>
                                            <Pill>
                                                <CreditCard className="mr-1.5 h-3.5 w-3.5" />
                                                Billing
                                            </Pill>
                                            <Pill>
                                                <Receipt className="mr-1.5 h-3.5 w-3.5" />
                                                Summary
                                            </Pill>
                                        </div>
                                        <h1 className="mt-3 text-2xl font-bold tracking-tight text-slate-900">Review your setup</h1>
                                        <p className="mt-1 max-w-3xl text-[13.5px] leading-6 text-slate-600">
                                            This page summarizes the client’s selected services and the monthly cost per service. Monthly charges
                                            are billed automatically to the client’s payment card.
                                        </p>
                                    </div>
                                </div>
                            </motion.div>

                            <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-[1fr_360px]">
                                {/* Left: Summary table */}
                                <div className="space-y-4">
                                    <Card className="rounded-2xl border-slate-200 bg-white/70 shadow-sm">
                                        <CardHeader className="pb-3">
                                            <CardTitle className="text-base">Selected services</CardTitle>
                                            <p className="text-[12.5px] leading-5 text-slate-600">
                                                Only active services are billed monthly. Usage-based fees (e.g., SMS sending) may be billed by the
                                                underlying provider.
                                            </p>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white/60">
                                                <div className="grid grid-cols-[1.3fr_.7fr_.6fr_.6fr] gap-0 border-b border-slate-200 bg-slate-50/60 px-4 py-3 text-[12px] font-semibold text-slate-700">
                                                    <div>Service</div>
                                                    <div>Group</div>
                                                    <div>Status</div>
                                                    <div className="text-right">/ month</div>
                                                </div>

                                                {items.map((it) => (
                                                    <div
                                                        key={it.id}
                                                        className={cn(
                                                            "grid grid-cols-[1.3fr_.7fr_.6fr_.6fr] gap-0 px-4 py-3",
                                                            "border-b border-slate-200 last:border-b-0",
                                                            it.active ? "bg-white" : "bg-white/40"
                                                        )}
                                                    >
                                                        <div className="min-w-0">
                                                            <div className="flex items-center gap-2">
                                                                <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-sm">
                                                                    {it.icon}
                                                                </span>
                                                                <div className="min-w-0">
                                                                    <div className="flex flex-wrap items-center gap-2">
                                                                        <div className="text-[13px] font-semibold text-slate-900">{it.title}</div>
                                                                        {it.mode ? <Chip>{it.mode}</Chip> : null}
                                                                    </div>
                                                                    <div className="mt-0.5 truncate text-[12.5px] text-slate-600">{it.subtitle}</div>
                                                                </div>
                                                            </div>
                                                            {it.notes?.length ? (
                                                                <div className="mt-2 flex flex-wrap gap-2">
                                                                    {it.notes.slice(0, 3).map((n, idx) => (
                                                                        <Chip key={idx}>{n}</Chip>
                                                                    ))}
                                                                </div>
                                                            ) : null}
                                                        </div>

                                                        <div className="flex items-center text-[12.5px] text-slate-700">{it.group}</div>

                                                        <div className="flex items-center">
                                                            {it.active ? (
                                                                <Badge className="rounded-full bg-emerald-600">Active</Badge>
                                                            ) : (
                                                                <Badge variant="outline" className="rounded-full text-slate-500">
                                                                    Disabled
                                                                </Badge>
                                                            )}
                                                        </div>

                                                        <div className="flex items-center justify-end text-[12.5px] font-semibold text-slate-900">
                                                            <span className="tabular-nums">{it.active ? it.monthly + " EUR" : 0} </span>
                                                        </div>
                                                    </div>
                                                ))}

                                                {/* Agent seats row */}
                                                <div className="grid grid-cols-[1.3fr_.7fr_.6fr_.6fr] gap-0 px-4 py-3 bg-white">
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-sm">
                                                                <BadgeCheck className="h-4.5 w-4.5 text-slate-800" />
                                                            </span>
                                                            <div className="min-w-0">
                                                                <div className="text-[13px] font-semibold text-slate-900">Agent seats</div>
                                                                <div className="mt-0.5 text-[12.5px] text-slate-600">
                                                                    {agentSeats} × {agentSeatPrice} {"EUR"} / seat
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center text-[12.5px] text-slate-700">Platform</div>
                                                    <div className="flex items-center">
                                                        <Badge className="rounded-full bg-emerald-600">Active</Badge>
                                                    </div>
                                                    <div className="flex items-center justify-end text-[12.5px] font-semibold text-slate-900">
                                                        <span className="tabular-nums">{agentSeatsMonthly + " EUR"}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                                <Notice tone="good" title="Monthly billing">
                                                    The total monthly cost is calculated from enabled services and active agent seats.
                                                </Notice>
                                                <Notice tone="info" title="Charged to the same card">
                                                    Service fees and agent seat billing ({agentSeatPrice} {client.price} per seat / month) are charged automatically to the
                                                    client’s payment card.
                                                </Notice>
                                            </div>
                                        </CardContent>
                                    </Card>

                                    <Card className="rounded-2xl border-slate-200 bg-white/70 shadow-sm">
                                        <CardHeader className="pb-3">
                                            <CardTitle className="text-base">Total</CardTitle>
                                        </CardHeader>
                                        <CardContent className="space-y-3">
                                            <Line label="Active services" value={<span className="font-semibold">{activeItems.length}</span>} />
                                            <Line label="Total per month" value={<span className="font-semibold"><span className="tabular-nums">{totalMonthly + " EUR"}</span></span>} />
                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                                <button className="h-10 inline-flex items-center gap-1 rounded-full bg-[var(--secondary-color)] text-[var(--primary-color)] text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition"
                                                    onClick={() => setShowPaymentSettings((v) => !v)}
                                                >
                                                    Go to payment settings
                                                    <ExternalLink className="ml-2 h-4 w-4" />
                                                </button>
                                                <button onClick={handleGoLiveClick} className="h-10 inline-flex items-center gap-1 rounded-full bg-[var(--primary-color)] text-white text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">Confirm & Go live</button>
                                            </div>
                                        </CardContent>
                                    </Card>

                                    {showPaymentSettings ? <PaymentSettings /> : null}
                                </div>

                                {/* Right: Billing card + checklist */}
                                <div className="space-y-4">
                                    <Card className="rounded-2xl border-slate-200 bg-white/70 shadow-sm">
                                        <CardHeader className="pb-3">
                                            <CardTitle className="text-base">Billing overview</CardTitle>
                                        </CardHeader>
                                        <CardContent className="space-y-3 text-[12.5px] leading-5 text-slate-700">
                                            <div className="rounded-xl border border-slate-200 bg-white/70 p-3">
                                                <div className="text-[12px] font-semibold text-slate-800">What is billed monthly</div>
                                                <div className="mt-1 text-slate-600">
                                                    Enabled services + platform modules. Usage fees (SMS, card processing, etc.) are charged by the
                                                    underlying provider.
                                                </div>
                                            </div>
                                            <div className="rounded-xl border border-slate-200 bg-white/70 p-3">
                                                <div className="text-[12px] font-semibold text-slate-800">When charges occur</div>
                                                <div className="mt-1 text-slate-600">Monthly, automatically, to the stored payment card.</div>
                                            </div>
                                            <div className="rounded-xl border border-slate-200 bg-white/70 p-3">
                                                <div className="text-[12px] font-semibold text-slate-800">Receipts & CSV</div>
                                                <div className="mt-1 text-slate-600">All billing events are available for export and reconciliation.</div>
                                            </div>
                                        </CardContent>
                                    </Card>

                                    <Card className="rounded-2xl border-slate-200 bg-white/70 shadow-sm">
                                        <CardHeader className="pb-3">
                                            <CardTitle className="text-base">Quick links</CardTitle>
                                        </CardHeader>
                                        <CardContent className="grid gap-2">
                                            <button className="inline-flex items-center gap-1 rounded-full justify-between bg-white text-[var(--primary-color)] border border-[var(--primary-color)] text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">
                                                Pricing details <ExternalLink className="h-4 w-4" />
                                            </button>
                                            <button className="inline-flex items-center gap-1 rounded-full justify-between bg-white text-[var(--primary-color)] border border-[var(--primary-color)] text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">
                                                Terms & billing <ExternalLink className="h-4 w-4" />
                                            </button>
                                            <button className="inline-flex items-center gap-1 rounded-full justify-between bg-white text-[var(--primary-color)] border border-[var(--primary-color)] text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">
                                                Payment method help <ExternalLink className="h-4 w-4" />
                                            </button>
                                        </CardContent>
                                    </Card>
                                </div>
                            </div>

                            {/* Sticky action bar */}
                            <div className="mt-5 sticky bottom-3 z-20">
                                <div className="rounded-2xl border border-slate-200 bg-white/80 p-3 shadow-lg backdrop-blur">
                                    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <Badge variant="outline" className="rounded-full">
                                                Active: {headerSummary}
                                            </Badge>
                                            <Badge variant="outline" className="rounded-full">
                                                Total / month: <span className="ml-1 font-semibold"><span className="tabular-nums">{totalMonthly + " EUR"}</span></span>
                                            </Badge>
                                            <Badge variant="outline" className="rounded-full">
                                                Billing method: Card
                                            </Badge>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button className="h-10 inline-flex items-center gap-1 rounded-full bg-[var(--secondary-color)] text-[var(--primary-color)] text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition"
                                                onClick={() => setShowPaymentSettings((v) => !v)}
                                            >
                                                Payment settings
                                            </button>
                                            <button className="h-10 inline-flex items-center gap-1 rounded-full bg-[var(--primary-color)] text-white text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">Save & Continue</button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mx-auto mt-4 max-w-7xl px-1 text-[12px] leading-5 text-slate-500">
                                Note: Amounts shown are example prices for the UI preview. Replace with your real price list and tenant
                                billing rules.
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div >
    );
}

// Small components

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
    return (
        <div className="mt-2 mb-1">
            <p className="text-[11px] font-semibold tracking-wide uppercase text-[#0C5FD1]">{title}</p>
            <p className="text-[10px] text-slate-500">{subtitle}</p>
        </div>
    );
}

function BlockToggle({
    category,
    option,
    selected,
    onToggle,
}: {
    category: keyof typeof blockOptions;
    option: { id: string; name: string; description: string };
    selected: boolean;
    onToggle: (category: keyof typeof blockOptions, id: string) => void;
}) {
    return (
        <button
            type="button"
            onClick={() => onToggle(category, option.id)}
            className={`w-full text-left rounded-xl border px-3 py-2 transition text-[11px] flex flex-col gap-1 ${selected
                ? "border-[var(--primary-color)]/80 bg-[var(--primary-color)]/10 text-[#062150] shadow-[0_0_0_1px_rgba(15,23,42,0.1)]"
                : "border-slate-200 bg-white/80 hover:border-[var(--primary-color)]/60 hover:bg-[#F4F8FF]"
                }`}
        >
            <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-[#083570]">{option.name}</p>
                <span
                    className={`h-4 w-4 inline-flex items-center justify-center rounded-md border text-[9px] ${selected
                        ? "border-[var(--primary-color)] bg-[var(--primary-color)] text-white"
                        : "border-slate-300 bg-[#F4F8FF] text-slate-400"
                        }`}
                >
                    {selected ? "✓" : "+"}
                </span>
            </div>
            <p className="text-[10px] text-slate-500">{option.description}</p>
        </button>
    );
}

// PREVIEW BUILDING BLOCKS

function HeroA() {
    return (
        <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white via-[#F4F8FF] to-[#E4F0FF] p-5 flex flex-col md:flex-row gap-6 items-center">
            <div className="flex-1">
                <p className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#E4F0FF]/90 border border-slate-200 text-[10px] text-[#0C5FD1] mb-3">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    Live · Powered by Chamcore microservices
                </p>
                <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-[#062150] mb-2">
                    Launch a consult-ready website in minutes.
                </h1>
                <p className="text-sm text-[#0C5FD1] mb-4 max-w-xl">
                    Pick your blocks, connect your phone numbers, video rooms and payment rails. Chamcore takes care of routing,
                    billing and reporting.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 text-xs">
                    <button className="px-4 py-2 rounded-full bg-[var(--primary-color)] text-white font-semibold hover:brightness-110 transition">
                        Book a demo
                    </button>
                    <button className="px-4 py-2 rounded-full border border-slate-300 text-[#0A4A99] hover:border-[var(--primary-color)] hover:text-[#0C5FD1] transition bg-white/80">
                        Try a sandbox site
                    </button>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-[11px] text-[#0C5FD1]">
                    <div>
                        <p className="font-semibold text-[#083570]">Microcomponents</p>
                        <p className="text-slate-500">Hero, agents, blog, video, CTA, checkout and more.</p>
                    </div>
                    <div>
                        <p className="font-semibold text-[#083570]">Connected stack</p>
                        <p className="text-slate-500">SMS, IVR, WebRTC and payments pre-wired.</p>
                    </div>
                </div>
            </div>
            <div className="w-full md:w-72 flex justify-center">
                <div className="w-full rounded-2xl border border-[var(--primary-color)]/40 bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF]/80 p-3 shadow-[0_0_40px_rgba(15,23,42,0.16)]">
                    <p className="text-[11px] text-[#0C5FD1] mb-2">Lead capture</p>
                    <div className="space-y-2 text-[11px]">
                        <input
                            className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 outline-none focus:border-[var(--primary-color)] text-[#083570] placeholder:text-slate-400"
                            placeholder="Name"
                        />
                        <input
                            className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 outline-none focus:border-[var(--primary-color)] text-[#083570] placeholder:text-slate-400"
                            placeholder="Email"
                        />
                        <select className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 outline-none focus:border-[var(--primary-color)] text-[#083570] text-[11px]">
                            <option>Channel focus</option>
                            <option>Advisory & coaches</option>
                            <option>Healthcare & telemedicine</option>
                            <option>Legal & compliance</option>
                            <option>Custom</option>
                        </select>
                        <button className="w-full mt-1 rounded-lg bg-[var(--primary-color)] text-white font-semibold py-1.5 hover:brightness-110 transition">
                            Generate my site
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}

function HeroB() {
    return (
        <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF] p-5 flex flex-col items-center text-center gap-4">
            <p className="text-[11px] text-[#0C5FD1] uppercase tracking-[0.14em]">Chamcore blueprint · Hero B</p>
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-[#062150] max-w-2xl">
                Drag microcomponents. Ship a fully wired, multi-channel website.
            </h1>
            <p className="text-sm text-[#0C5FD1] max-w-xl">
                Swap hero layouts, agent cards, blogs and streaming blocks without touching code. Your dev team only connects
                the APIs once.
            </p>
            <div className="aspect-video w-full max-w-xl rounded-2xl border border-slate-200 bg-gradient-to-tr from-slate-900 via-slate-800 to-cyan-900/40 flex items-center justify-center text-[11px] text-[#E7F4FF]">
                <div className="px-4 py-2 rounded-full bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF]/80 border border-slate-300 flex items-center gap-2 text-[#062150]">
                    <span className="h-2 w-2 rounded-full bg-emerald-400" />
                    Demo video – Building a site in 90 seconds
                </div>
            </div>
        </section>
    );
}

function HeroC() {
    return (
        <section className="rounded-2xl border border-slate-200 bg-gradient-to-r from-[var(--primary-color)]/20 via-[#F4F8FF] to-[#E4F0FF] p-5 flex flex-col md:flex-row gap-6 items-center">
            <div className="flex-1 space-y-2">
                <p className="text-[11px] text-[#0C5FD1] uppercase tracking-[0.16em]">Hero C · Split layout</p>
                <h1 className="text-2xl md:text-3xl font-semibold text-[#062150]">
                    Templates for advisors, clinics and marketplaces.
                </h1>
                <p className="text-sm text-[#0A4A99] max-w-xl">
                    Pick a pre-built industry template or start from a blank Chamcore blueprint. All include routing, booking, and
                    payment flows.
                </p>
                <ul className="text-[11px] text-[#083570] grid grid-cols-2 gap-2 mt-3">
                    <li className="flex items-center gap-1.5">
                        <span className="h-3 w-3 rounded-full bg-[var(--primary-color)]/80" />
                        Advisors & coaches
                    </li>
                    <li className="flex items-center gap-1.5">
                        <span className="h-3 w-3 rounded-full bg-[var(--primary-color)]/80" />
                        Legal & finance
                    </li>
                    <li className="flex items-center gap-1.5">
                        <span className="h-3 w-3 rounded-full bg-[var(--primary-color)]/80" />
                        Healthcare & telemed
                    </li>
                    <li className="flex items-center gap-1.5">
                        <span className="h-3 w-3 rounded-full bg-[var(--primary-color)]/80" />
                        Custom service hubs
                    </li>
                </ul>
            </div>
            <div className="w-full md:w-64">
                <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF]/80 p-3 space-y-2 text-[11px] text-[#0A4A99]">
                    <p className="font-semibold mb-1">Choose template</p>
                    <div className="space-y-1">
                        <TemplateBadge label="Advisor hub" />
                        <TemplateBadge label="Telehealth pod" />
                        <TemplateBadge label="Legal boutique" />
                        <TemplateBadge label="Custom" />
                    </div>
                </div>
            </div>
        </section>
    );
}

function TemplateBadge({ label }: { label: string }) {
    return (
        <div className="flex items-center justify-between px-2 py-1 rounded-lg border border-slate-200 bg-white">
            <span className="text-[#083570]">{label}</span>
            <span className="text-[10px] text-[#0C5FD1]">Preview</span>
        </div>
    );
}

function AgentsGrid() {
    return (
        <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF] p-4">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <p className="text-xs font-semibold text-[#083570]">Featured advisors</p>
                    <p className="text-[11px] text-slate-500">Auto-syncs from Chamcore agent directory.</p>
                </div>
                <button className="text-[11px] px-2 py-1 rounded-full border border-slate-300 text-[#0C5FD1] hover:border-[var(--primary-color)] hover:text-[#0C5FD1] transition bg-white/80">
                    View all
                </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-[11px]">
                {["Emma", "Jonas", "Lena"].map((name, idx) => (
                    <div key={name} className="rounded-xl border border-slate-200 bg-white p-3 flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                            <div className="h-9 w-9 rounded-full bg-[#9BC3FF]" />
                            <div>
                                <p className="font-semibold text-[#083570]">
                                    {name} {idx === 0 ? "Berg" : idx === 1 ? "Nielsen" : "Holt"}
                                </p>
                                <p className="text-[10px] text-slate-500">Senior advisor · Remote</p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-1">
                            <Tag label="Private clients" />
                            <Tag label="Business" />
                            {idx === 0 && <Tag label="Vipps" />}
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-1">
                            <button className="flex-1 px-2 py-1 rounded-full bg-[var(--primary-color)] text-white font-semibold">
                                Book
                            </button>
                            <button className="px-2 py-1 rounded-full border border-slate-300 text-[#0A4A99] bg-white/80">
                                Profile
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function AgentsList() {
    return (
        <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF] p-4">
            <p className="text-xs font-semibold text-[#083570] mb-2">Our advisors</p>
            <div className="space-y-2 text-[11px]">
                {["Line", "Marius", "Sofie"].map((name, idx) => (
                    <div
                        key={name}
                        className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2"
                    >
                        <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-[#9BC3FF]" />
                            <div>
                                <p className="font-semibold text-[#083570]">
                                    {name} {idx === 0 ? "Hansen" : idx === 1 ? "Lunde" : "Rye"}
                                </p>
                                <p className="text-[10px] text-slate-500">Specialist · Nordic clients</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border border-emerald-500/60 bg-emerald-50 text-emerald-700">
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                                Available today
                            </span>
                            <button className="px-2 py-1 rounded-full border border-slate-300 text-[#0A4A99] bg-white/80">
                                Book
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function Tag({ label }: { label: string }) {
    return (
        <span className="px-2 py-0.5 rounded-full border border-slate-300 text-[10px] text-[#0C5FD1] bg-[#E4F0FF]">
            {label}
        </span>
    );
}

function BlogGrid() {
    return (
        <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF] p-4">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <p className="text-xs font-semibold text-[#083570]">Latest insights</p>
                    <p className="text-[11px] text-slate-500">Pulls from /blog API endpoint.</p>
                </div>
                <button className="text-[11px] text-[#0C5FD1]">Manage posts →</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px]">
                {[1, 2, 3].map((i) => (
                    <article key={i} className="rounded-xl border border-slate-200 bg-white p-3 flex flex-col gap-2">
                        <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Advisory</p>
                        <h3 className="font-semibold text-[#083570]">
                            How to move from phone-only to full digital delivery
                        </h3>
                        <p className="text-[10px] text-slate-500">
                            Use Chamcore to add video, chat and payments in one go.
                        </p>
                        <p className="text-[10px] text-slate-400">5 min read · Updated weekly</p>
                    </article>
                ))}
            </div>
        </section>
    );
}

function BlogList() {
    return (
        <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF] p-4">
            <p className="text-xs font-semibold text-[#083570] mb-2">From the blog</p>
            <div className="space-y-2 text-[11px]">
                {[1, 2, 3].map((i) => (
                    <article
                        key={i}
                        className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2"
                    >
                        <div>
                            <p className="text-[10px] text-slate-500 mb-0.5">CHANNEL DESIGN</p>
                            <h3 className="font-semibold text-[#083570]">Booking, IVR and payments in one UX</h3>
                            <p className="text-[10px] text-slate-500">
                                Why single-channel tools create friction for clients.
                            </p>
                        </div>
                        <p className="text-[10px] text-slate-400 whitespace-nowrap">7 min read</p>
                    </article>
                ))}
            </div>
        </section>
    );
}

function VideoLivestream() {
    return (
        <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF] p-4">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <p className="text-xs font-semibold text-[#083570]">Live sessions</p>
                    <p className="text-[11px] text-slate-500">Chamcore WebRTC + IVR fallback.</p>
                </div>
                <button className="text-[11px] text-[#0C5FD1]">Manage events →</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px]">
                {["Today", "Tomorrow"].map((label, idx) => (
                    <div key={label} className="rounded-xl border border-slate-200 bg-white p-3">
                        <p className="text-[10px] text-slate-500 mb-1">{label} · 19:00 CET</p>
                        <h3 className="font-semibold text-[#083570]">Open Q&A with our advisory team</h3>
                        <p className="text-[10px] text-slate-500 mb-2">
                            Join from browser or dial-in via local phone number.
                        </p>
                        <div className="flex items-center justify-between">
                            <button className="px-3 py-1 rounded-full bg-[var(--primary-color)] text-white font-semibold">
                                {idx === 0 ? "Join waiting room" : "Reserve spot"}
                            </button>
                            <p className="text-[10px] text-slate-400">100 seats · 34 booked</p>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function VideoLibrary() {
    return (
        <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF] p-4">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <p className="text-xs font-semibold text-[#083570]">Video library</p>
                    <p className="text-[11px] text-slate-500">On-demand content powered by Chamcore streaming.</p>
                </div>
                <div className="flex items-center gap-2 text-[11px]">
                    <select className="rounded-full border border-slate-300 bg-white px-2 py-1 text-[#0A4A99]">
                        <option>All categories</option>
                    </select>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px]">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                        <div className="aspect-video bg-[#9BC3FF] flex items-center justify-center text-[10px] text-slate-600">
                            Video thumbnail
                        </div>
                        <div className="p-3 space-y-1">
                            <p className="font-semibold text-[#083570]">How Chamcore routing works</p>
                            <p className="text-[10px] text-slate-500">Short walkthrough of our comms engine.</p>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function CtaSimple() {
    return (
        <section className="rounded-2xl border border-slate-200 p-4 flex flex-col sm:flex-row items-center justify-between gap-3"
            style={{ backgroundImage: "linear-gradient(to right, var(--primary-color), var(--secondary-color))" }}>
            <div>
                <p className="text-xs font-semibold text-[#062150]">Ready to switch on digital delivery?</p>
                <p className="text-[11px] text-[#083570] max-w-md">
                    One Chamcore login gives you website, booking, IVR, video and payments in a single stack.
                </p>
            </div>
            <div className="flex flex-wrap gap-2 text-[11px]">
                <button className="px-4 py-1.5 rounded-full bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF] text-[#062150] font-semibold">
                    Talk to sales
                </button>
                <button className="px-4 py-1.5 rounded-full border border-slate-200 text-[#062150] bg-white/80">
                    Explore pricing
                </button>
            </div>
        </section>
    );
}

function CtaMulti() {
    return (
        <section className="rounded-2xl border border-slate-200 p-4"
            style={{ backgroundImage: "linear-gradient(to right, var(--primary-color), var(--secondary-color))" }}>
            <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-[#083570]">Pick how you want to start</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px]">
                <CtaCard title="Advisors" body="Solo or small teams that want a ready-made advisory funnel." />
                <CtaCard title="Clinics" body="Healthcare teams connecting pods, rooms and remote staff." />
                <CtaCard title="Custom" body="Marketplaces, platforms and bespoke architectures." />
            </div>
        </section>
    );
}

function CtaCard({ title, body }: { title: string; body: string }) {
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-3 flex flex-col gap-2">
            <p className="font-semibold text-[#083570]">{title}</p>
            <p className="text-[10px] text-slate-500">{body}</p>
            <button className="mt-auto self-start px-3 py-1 rounded-full bg-[var(--primary-color)] text-white font-semibold text-[11px]">
                Choose
            </button>
        </div>
    );
}
